import {
  extractTableData,
  formatExportLocationDisplay,
  stripHtmlToPlainTextForExport,
  formatFootnotesBlockForTextExport,
} from './exportData';
import { buildExportSourceUrl, getExportDataSourceDisplayName } from './urlRouting';
import { getLocationFootnotesMap, getSortedFootnoteIndicesFromMap } from './locationFootnotes';

/** Set in applyExportFixes (full-table clone); read after html2canvas in captureElement. */
let pendingExportRowIntervalsCss = null;

/** Desktop .main-table-top font-size (Tables.css); source footer is 1px smaller. */
const MAIN_TABLE_TOP_FONT_PX = 14;
const SOURCE_FOOTER_FONT_PX = MAIN_TABLE_TOP_FONT_PX - 1;
/** One CSS px smaller than source line at any capture scale (12 vs 13 at scale 1). */
const COPYRIGHT_FOOTER_FONT_PX = SOURCE_FOOTER_FONT_PX - 2;
/** Horizontal inset for source line (px at scale 1); adjust if table gutters change. */
const SOURCE_X_PAD_PX = 10;

/** Same wording as Footer.jsx / SideMenu — raster PDF/PNG/print source strip. */
function getKrimeExportCopyrightLine() {
  return `© ${new Date().getFullYear()} KRIME Inc. All rights reserved.`;
}

/** US Letter portrait @ 96 CSS px/in — 8.5×11 in. */
const LETTER_PAGE_W = Math.round(8.5 * 96);
const LETTER_PAGE_H = Math.round(11 * 96);

/** Wider than 1023px so html2canvas applies desktop/table layout in the clone (not narrow mobile). */
const EXPORT_LAYOUT_VIEWPORT_W = 1280;

/** Extra px when locking non-table scroll regions (e.g. charts) so borders aren’t clipped in raster output. */
const EXPORT_VIEWPORT_SLACK_PX = 14;
/** Tiny pad on `.main-table-scroll-container` only — html2canvas edge; height stays the scroll viewport (clientHeight). */
const EXPORT_TABLE_VIEWPORT_RASTER_PAD_PX = 4;
/** Inner drawable width for charts with .chart-container horizontal padding (inject matches Tables export). */
const EXPORT_CHART_INNER_W = LETTER_PAGE_W - 28;
/** Extra height on html2canvas `windowHeight` — the renderer can clip the last few CSS px if this matches content exactly. */
const EXPORT_HTML2CANVAS_WINDOW_PAD_PX = 40;
/**
 * Location Name scores gauge: live DOM scrollHeight can be short of the clone (letter-width reflow),
 * so windowHeight clips the last row before the stacked score-footnote canvas — desktop (header in
 * capture) is more sensitive than mobile body-only capture.
 */
const EXPORT_SCORES_GAUGE_EXTRA_PAD_PX = 120;
/** Extra height for full-table PDF/print capture only (scrollHeight + html2canvas safety). */
const EXPORT_FULL_TABLE_RASTER_EXTRA_PX = 160;
/** Mobile: `.main-table-scroll-container` scrollHeight can match the viewport (underestimate). */
const EXPORT_FULL_TABLE_MOBILE_EXTRA_PAD_PX = 280;
/**
 * Export clone uses letter-width layout; row heights can exceed live DOM. Applied to full-table
 * height before html2canvas windowHeight (never clip on-screen rows).
 */
const EXPORT_FULL_TABLE_REFLOW_HEADROOM_MULT = 1.22;
/** Reports: letter-width clone reflow + flex parent slack on small viewports — extra html2canvas windowHeight. */
const EXPORT_REPORTS_CAPTURE_HEADROOM_PX = 160;

/** US Letter @ 72 pt/in — matches jsPDF default `format: 'letter'`. */
const LETTER_PAGE_W_PT = 612;
const LETTER_PAGE_H_PT = 792;
/** PDF/print margins (pt) for tiled full-table raster — 0 = edge-to-edge on letter. */
const EXPORT_PDF_PRINT_MARGIN_PT = 0;

/**
 * html2canvas often crowds hyphens against the following glyph (clone-only string).
 */
function addHairSpaceAfterHyphens(s) {
  return s.replace(/-(?=[A-Za-z0-9])/g, '-\u200a');
}

/** Tight comma directly before a letter, e.g. "City,NY" (clone-only). */
function addHairSpaceAfterTightCommas(s) {
  return s.replace(/,(?=[A-Za-z])/g, ',\u200a');
}

function normalizeLocationTextForExportRaster(s) {
  return addHairSpaceAfterTightCommas(addHairSpaceAfterHyphens(s));
}

function pickTitleSnapshotStyle(cs) {
  return {
    fontSize: cs.fontSize,
    fontFamily: cs.fontFamily,
    fontWeight: cs.fontWeight,
    fontStyle: cs.fontStyle,
    letterSpacing: cs.letterSpacing,
    lineHeight: cs.lineHeight,
    textAlign: cs.textAlign,
    whiteSpace: cs.whiteSpace,
    wordBreak: cs.wordBreak,
    overflowWrap: cs.overflowWrap,
    hyphens: cs.hyphens,
    fontFeatureSettings: cs.fontFeatureSettings,
    fontVariantNumeric: cs.fontVariantNumeric,
    display: cs.display,
    transform: cs.transform,
    textTransform: cs.textTransform,
    color: cs.color,
    width: cs.width,
    maxWidth: cs.maxWidth,
    minWidth: cs.minWidth,
  };
}

/** Live DOM only — superscript text before clone replaces innerHTML. */
function readFootnoteNumbersFromLiveTitleDiv(liveTextDiv) {
  if (!liveTextDiv?.querySelectorAll) return null;
  const sups = liveTextDiv.querySelectorAll('.footnotes-button .footnote-superscript');
  if (!sups.length) return null;
  const nums = Array.from(sups)
    .map((s) => parseInt(String(s.textContent || '').trim(), 10))
    .filter((n) => !Number.isNaN(n));
  return nums.length ? nums : null;
}

/**
 * Flatten header title for html2canvas: one text run + optional superscript footnote refs.
 * Plain `text` alone drops `<sup>` nodes from the React tree — refs must be rebuilt here.
 */
function applyTitleSnapshotToClone(div, text, style, footnoteNumbers = null) {
  const doc = div.ownerDocument;
  while (div.firstChild) div.removeChild(div.firstChild);

  const nums = Array.isArray(footnoteNumbers) && footnoteNumbers.length > 0 ? footnoteNumbers : null;

  if (nums) {
    const lastCommaIndex = text.lastIndexOf(',');
    let beforeComma;
    let suffix;
    if (lastCommaIndex !== -1) {
      beforeComma = text.substring(0, lastCommaIndex + 1);
      suffix = text.substring(lastCommaIndex + 1).trim();
    } else {
      beforeComma = text;
      suffix = '';
    }
    if (beforeComma) div.appendChild(doc.createTextNode(beforeComma));
    const suffixWrap = doc.createElement('span');
    suffixWrap.className = 'location-suffix';
    suffixWrap.style.whiteSpace = 'nowrap';
    if (suffix) suffixWrap.appendChild(doc.createTextNode(suffix));
    const fnWrap = doc.createElement('span');
    fnWrap.className = 'footnotes-button';
    fnWrap.setAttribute('aria-hidden', 'true');
    const fs = SOURCE_FOOTER_FONT_PX;
    nums.forEach((num, index) => {
      const sup = doc.createElement('sup');
      sup.className = 'footnote-superscript';
      sup.style.fontSize = `${fs}px`;
      sup.textContent = String(num);
      fnWrap.appendChild(sup);
      if (index < nums.length - 1) {
        const comma = doc.createElement('sup');
        comma.className = 'footnote-comma';
        comma.style.fontSize = `${fs}px`;
        comma.textContent = ',';
        fnWrap.appendChild(comma);
      }
    });
    suffixWrap.appendChild(fnWrap);
    div.appendChild(suffixWrap);
  } else {
    div.appendChild(doc.createTextNode(text));
  }

  if (!style) return;
  Object.assign(div.style, style);
  div.style.textRendering = 'geometricPrecision';
  div.style.fontKerning = 'normal';
  div.style.webkitFontSmoothing = 'antialiased';
  div.style.fontVariantLigatures = 'none';
  div.style.fontFeatureSettings = '"liga" 0, "clig" 0';
  const ls = style.letterSpacing;
  if (!ls || ls === 'normal') div.style.letterSpacing = '0.02em';
}

// ─── Export-only desktop-parity stylesheet ────────────────────────────────────
// Injected into the html2canvas clone document. Forces every viewport-dependent
// value to its Tables.css / AnalysisHeader.css *desktop base* so that extracts
// are pixel-identical regardless of the device screen width or height.
function injectExportDesktopStyles(doc) {
  if (doc.getElementById('krime-export-desktop')) return;
  const s = doc.createElement('style');
  s.id = 'krime-export-desktop';
  s.textContent = `
    /* ── Disable mobile text-size inflation ───────────────────────────── */
    html {
      -webkit-text-size-adjust: 100% !important;
      text-size-adjust: 100% !important;
    }

    /* ── AnalysisHeader: AnalysisHeader.css base (desktop ≥768) ─────── */
    .analysis-header {
      padding: 8px 16px 8px 0 !important;
      position: relative !important;
      top: auto !important;
      margin-top: 1px !important;
    }
    .analysis-header .analysis-location {
      margin-top: -8px !important;
    }
    .analysis-header .location-selector {
      max-width: none !important;
      height: auto !important;
    }
    .analysis-header .auto-resize-container {
      height: auto !important;
    }
    .analysis-header .location-title-container .auto-resize-container > div {
      font-size: 24px !important;
      line-height: 1.2 !important;
      white-space: nowrap !important;
      display: block !important;
      width: auto !important;
      text-align: center !important;
    }
    .analysis-header .location-title-container {
      justify-content: center !important;
      margin-left: 0 !important;
    }
    .analysis-header .location-selector {
      justify-content: center !important;
      margin-left: 28px !important;
    }
    /* Footnote refs: same white as location text on the header gradient (matches live AnalysisHeader). */
    .analysis-header .location-title-container .footnote-superscript,
    .analysis-header .location-title-container .footnote-comma {
      font-size: ${SOURCE_FOOTER_FONT_PX}px !important;
      font-variant-numeric: lining-nums tabular-nums !important;
      color: #ffffff !important;
      -webkit-text-fill-color: #ffffff !important;
    }
    .analysis-header .location-title-container .footnotes-button {
      display: inline !important;
      pointer-events: none !important;
      background: none !important;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      box-shadow: none !important;
      vertical-align: baseline !important;
      color: #ffffff !important;
      -webkit-text-fill-color: #ffffff !important;
      -webkit-appearance: none !important;
      appearance: none !important;
    }

    /* Export: center location under KRIME + tab (chevron/alert removed from layout). */
    .analysis-header .analysis-header-content {
      align-items: center !important;
    }
    .analysis-header .location-selector > .chevron-down {
      display: none !important;
    }
    .analysis-header .analysis-alert-button {
      display: none !important;
    }
    .analysis-header .location-selector {
      margin-left: 0 !important;
      margin-right: 0 !important;
      gap: 0 !important;
      justify-content: center !important;
      max-width: none !important;
    }
    .analysis-header .analysis-location {
      justify-content: center !important;
      width: 100% !important;
    }

    .chart-legend-chevron {
      display: none !important;
    }
    .main-table-top .comparison-menu-button svg,
    .main-table-top .population-menu-button svg {
      display: none !important;
    }

    /* Extract only: gap under AnalysisHeader (clone — not live UI). Tables already have .main-table-top spacing. */
    .split-view-content > .data-view-container {
      margin-top: 10px !important;
    }
    .split-view-content > .data-view-container:has(.main-table-top) {
      margin-top: 0 !important;
    }

    /* ── Tables: Tables.css base (desktop ≥768 AND height ≥700) ─────── */
    .main-table {
      font-size: 14px !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
    }
    .main-table th,
    .main-table td {
      font-size: 14px !important;
      line-height: 1.2 !important;
      box-sizing: border-box !important;
      vertical-align: middle !important;
    }
    .main-table td {
      border-bottom: 1px solid var(--border-color) !important;
    }
    .main-table th {
      padding: 4px 8px !important;
      height: 27px !important;
      border-bottom: 2px solid var(--border-color) !important;
    }
    .main-table th:first-child,
    .main-table th:last-child {
      padding: 4px 12px !important;
    }
    .main-table:not(.location-name-table) td {
      padding: 8px 12px !important;
    }
    .main-table.location-name-table td {
      padding-right: 10px !important;
    }
    .main-table td:nth-child(2) {
      padding-left: 12px !important;
    }
    .main-table th:first-child,
    .main-table td:first-child {
      padding-right: 10px !important;
    }
    .main-table .rank-cell {
      padding-right: 0 !important;
    }
    .main-table .weight-cell {
      padding-right: 0 !important;
    }
    .main-table .crime-type-cell {
      padding-right: 0 !important;
    }
    .main-table .number-cell.nation {
      padding-right: 30px !important;
    }

    /* LocationType: collapse removes phantom seams; row bg avoids per-th gradient lines in raster. */
    .location-type-table {
      border-collapse: collapse !important;
    }
    .location-type-table thead tr {
      background: linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)) var(--navy) !important;
    }
    .location-type-table thead th {
      background: transparent !important;
      overflow: hidden !important;
    }

    /* LocationType: strip vertical borders; only rule after col 2 (sticky split). */
    .location-type-table th,
    .location-type-table td {
      border-left: none !important;
      border-right: none !important;
    }
    .location-type-table th:nth-child(2),
    .location-type-table td:nth-child(2) {
      border-right: 1px solid var(--border-color) !important;
    }
    .location-type-table th {
      border-bottom: 2px solid var(--border-color) !important;
    }
    .location-type-table td {
      border-bottom: 1px solid var(--border-color) !important;
    }

    /* ── Summary row + misc elements ──────────────────────────────────── */
    .main-table-top {
      font-size: 14px !important;
      line-height: 1.2 !important;
      box-sizing: border-box !important;
      padding: 10px 0 8px !important;
    }
    .crime-type-content {
      gap: 4px !important;
      line-height: 1.2 !important;
    }
    .stacked-header {
      font-size: 11px !important;
      line-height: 1.2 !important;
    }
    .single-line-header {
      height: 26px !important;
      line-height: 1.2 !important;
    }
    .comparison-dropdown-container {
      left: auto !important;
    }
    .level-1 .crime-type-content {
      padding-left: 24px !important;
    }
    .level-2 .crime-type-content {
      padding-left: 36px !important;
    }

    /* ReportCards: hide ellipsis menu triggers (export clone only). */
    .report-card-menu-container {
      display: none !important;
    }
    .split-view-content .report-cards-container {
      width: 100% !important;
      max-width: 100% !important;
    }
    /* Unwrap title ellipsis in raster so full card text contributes to layout height. */
    .split-view-content .report-card-text h3 {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
    }

    /* Charts: use full extract width; extra bottom space for x-axis tick labels. */
    .split-view-content .data-view-container,
    .split-view-content .data-view-content,
    .split-view-content .data-view-scroll-area {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    .split-view-content .chart-container.scores-chart {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
    }
    .split-view-content .chart-container:not(.scores-chart) {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
      padding-left: 0 !important;
      padding-right: 28px !important;
      padding-bottom: 48px !important;
    }
    .split-view-content .chart-container:not(.scores-chart) .chart-content {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }
    .split-view-content .recharts-responsive-container {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
    .split-view-content .recharts-wrapper {
      width: 100% !important;
      max-width: 100% !important;
    }
    /* Recharts: no tooltip or hover cursor in raster exports. */
    .split-view-content .recharts-tooltip-wrapper,
    .split-view-content .recharts-default-tooltip,
    .split-view-content .custom-tooltip {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .split-view-content .recharts-tooltip-cursor {
      display: none !important;
      opacity: 0 !important;
      visibility: hidden !important;
    }
  `;
  (doc.head || doc.documentElement).appendChild(s);
}

/**
 * html2canvas often omits or mis-renders <button> footnote clusters; use inline spans for capture only.
 */
function replaceFootnotesButtonsWithSpansInExportClone(doc) {
  doc.querySelectorAll('.footnotes-button').forEach((btn) => {
    if (btn.tagName !== 'BUTTON') return;
    const span = doc.createElement('span');
    span.className = btn.className;
    span.setAttribute('aria-hidden', 'true');
    while (btn.firstChild) span.appendChild(btn.firstChild);
    btn.parentNode.replaceChild(span, btn);
  });
}

// Icon / logo desktop sizing via inline styles (CSS !important on transforms distorts the SVG stack).
function applyExportIconLogoDesktop(doc) {
  doc.querySelectorAll('.analysis-header .analysis-icon').forEach((el) => {
    el.style.left = '12px';
    el.style.top = '50%';
    el.style.transform = 'translateY(-50%)';
    el.style.height = '54px';
    el.style.width = '54px';
  });
  doc.querySelectorAll('.analysis-header .analysis-icon svg').forEach((svg) => {
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.style.width = '32px';
    svg.style.height = '32px';
  });
  doc.querySelectorAll('.analysis-header .analysis-logo').forEach((el) => {
    el.style.transform = 'scale(0.8)';
    el.style.transformOrigin = 'center center';
    el.style.marginTop = '-8px';
  });
}

/** Letter-width chart sizing so mobile extracts match desktop (Recharts often keeps narrow inline width). */
function applyExportChartLetterWidths(doc) {
  doc.querySelectorAll('.split-view-content .chart-container.scores-chart').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container:not(.scores-chart)').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container.scores-chart .recharts-responsive-container').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container:not(.scores-chart) .recharts-responsive-container').forEach((el) => {
    el.style.setProperty('width', `${EXPORT_CHART_INNER_W}px`, 'important');
    el.style.setProperty('max-width', `${EXPORT_CHART_INNER_W}px`, 'important');
    el.style.setProperty('min-width', `${EXPORT_CHART_INNER_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container.scores-chart .recharts-wrapper').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container:not(.scores-chart) .recharts-wrapper').forEach((el) => {
    el.style.setProperty('width', `${EXPORT_CHART_INNER_W}px`, 'important');
    el.style.setProperty('max-width', `${EXPORT_CHART_INNER_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container.scores-chart .chart-content').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
  });
  doc.querySelectorAll('.split-view-content .chart-container:not(.scores-chart) .chart-content').forEach((el) => {
    el.style.setProperty('width', `${EXPORT_CHART_INNER_W}px`, 'important');
    el.style.setProperty('max-width', `${EXPORT_CHART_INNER_W}px`, 'important');
    el.style.setProperty('min-width', `${EXPORT_CHART_INNER_W}px`, 'important');
  });
}

/**
 * html2canvas uses windowWidth=1280 so desktop split CSS applies in the clone (50% split +
 * inactive map). Hide the map and relax flex so the active pane can sit at letter width.
 *
 * Do not set width:100% !important on .split-view-content here — applyExportFixes assigns
 * LETTER_PAGE_W with !important; width:100% would win over non-important px and leave a ~1280px
 * wide flex row with an ~816px chart centered inside (huge side margins vs mobile-looking exports).
 */
function applyMobileSplitViewWidthForExportClone(doc) {
  doc.querySelectorAll('.split-view').forEach((el) => {
    el.style.setProperty('flex', '1 1 auto', 'important');
    el.style.setProperty('min-width', '0', 'important');
  });
  doc.querySelectorAll('.split-view .split-view-map:not([data-active="true"])').forEach((el) => {
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('flex', '0 0 0', 'important');
    el.style.setProperty('width', '0', 'important');
    el.style.setProperty('min-width', '0', 'important');
    el.style.setProperty('max-width', '0', 'important');
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('padding', '0', 'important');
    el.style.setProperty('overflow', 'hidden', 'important');
  });
  doc.querySelectorAll('.split-view .split-view-content[data-active="true"]').forEach((el) => {
    el.style.setProperty('flex', '1 1 auto', 'important');
    el.style.setProperty('min-width', '0', 'important');
  });
}

function isMobileAnalysisLayoutActiveInLiveDom() {
  if (typeof document === 'undefined') return false;
  const m = document.querySelector('.mobile-analysis-header');
  return !!(m && window.getComputedStyle(m).display !== 'none');
}

function syncRechartsSurfaceSvgWidths(doc) {
  doc.querySelectorAll('.recharts-wrapper').forEach((wrap) => {
    const chartRoot = wrap.closest('.chart-container');
    const scores = chartRoot?.classList.contains('scores-chart');
    const targetW = scores ? LETTER_PAGE_W : EXPORT_CHART_INNER_W;
    let w = Math.round(wrap.getBoundingClientRect().width) || wrap.clientWidth || 0;
    if (w < targetW - 2) w = targetW;
    w = Math.max(1, w);
    wrap.querySelectorAll('svg.recharts-surface').forEach((svg) => {
      const prevW = parseFloat(svg.getAttribute('width')) || w;
      const prevH = parseFloat(svg.getAttribute('height'));
      svg.setAttribute('width', String(w));
      if (prevH > 0 && prevW > 0) {
        svg.setAttribute('height', String(Math.round((prevH / prevW) * w)));
      }
    });
  });
}

/**
 * PDF/print: expand comparison / historical tables across letter pages (tiled) so rows are not clipped.
 * Non-historical Location Name tables + Reports use viewport capture + single-page fit with footers.
 */
function shouldUseFullTableRasterExport(exportContext) {
  if (!exportContext || exportContext.selectedView !== 'table') return false;
  if (exportContext.activeTab === 'reports') return false;
  if (exportContext.view === 'locationName' && !exportContext.isHistorical) return false;
  const el = exportContext.captureRef?.current;
  if (!el) return false;
  return el.querySelector('.main-table-scroll-container table.main-table') != null;
}

/** LocationType / Historical data tables (not e.g. location-name tables). */
function isLocationTypeOrHistoricalTableScrollContainer(el) {
  const table = el?.querySelector?.('table.main-table');
  return !!(
    table &&
    (table.classList.contains('location-type-table') || table.classList.contains('historical-table'))
  );
}

/** PNG: match on-screen table panel for LocationType / Historical tables. */
function shouldUseViewportTablePngExport(exportContext) {
  if (!exportContext || exportContext.selectedView !== 'table') return false;
  if (exportContext.activeTab === 'reports') return false;
  const el = exportContext.captureRef?.current;
  if (!el) return false;
  const scrollContainers = Array.from(
    el.querySelectorAll('.main-table-scroll-container')
  );
  return scrollContainers.some(isLocationTypeOrHistoricalTableScrollContainer);
}

/** Scroll viewport on screen: what fits in the box without scrolling (clientHeight + tiny raster pad). */
function measureTableScrollViewportPx(el) {
  const r = el.getBoundingClientRect();
  return Math.ceil(Math.max(el.clientHeight, r.height)) + EXPORT_TABLE_VIEWPORT_RASTER_PAD_PX;
}

/**
 * Minimum bottom edge (px from captureRoot top) that includes all rendered table rows.
 * `.main-table-wrapper` rect often matches the viewport (flex), not content — do not use it alone.
 */
function measureFullTableRasterMinBottomPx(captureRoot) {
  if (!captureRoot?.getBoundingClientRect) return 0;
  const rootRect = captureRoot.getBoundingClientRect();
  const table = captureRoot.querySelector('table.main-table');
  const scroll = captureRoot.querySelector('.main-table-scroll-container');
  if (!table || !scroll) return 0;
  const scrollTopRel = scroll.getBoundingClientRect().top - rootRect.top;
  let sumTr = 0;
  table.querySelectorAll('thead tr, tbody tr').forEach((tr) => {
    sumTr += Math.ceil(tr.offsetHeight || tr.getBoundingClientRect().height || 0);
  });
  const tableH = Math.max(
    table.scrollHeight,
    table.offsetHeight,
    sumTr,
    Math.ceil(table.getBoundingClientRect().height),
  );
  return Math.ceil(scrollTopRel + tableH + EXPORT_VIEWPORT_SLACK_PX);
}

/**
 * Scroll positions + viewport sizes (same DOM order as clone querySelectorAll).
 * Table panes: visible viewport from screen size; charts: client box + slack.
 */
function measureScrollPaneViewportPx(el) {
  if (el.classList?.contains('main-table-scroll-container')) {
    return measureTableScrollViewportPx(el);
  }
  const r = el.getBoundingClientRect();
  const viewportH = Math.max(el.clientHeight, r.height);
  return Math.ceil(viewportH) + EXPORT_VIEWPORT_SLACK_PX;
}

/**
 * Report cards use `height: calc(100% - …)` in CSS, so scrollHeight/clientHeight match the viewport.
 * For raster export, height must follow the card grid only (sum of row geometry, not the flex parent).
 * On small screens cards may use `overflow: hidden` and shrink — use each card's scrollHeight so clipped
 * content is included in the export height budget.
 */
function measureReportCardsContentHeight(rcEl) {
  if (!rcEl?.getBoundingClientRect) return 0;
  const cards = rcEl.querySelectorAll('.report-card');
  const rcRect = rcEl.getBoundingClientRect();
  if (!cards.length) {
    return Math.ceil(rcRect.height);
  }
  let maxBottomRel = 0;
  cards.forEach((card) => {
    const br = card.getBoundingClientRect();
    const topRel = br.top - rcRect.top;
    const fullH = Math.max(
      card.scrollHeight,
      card.offsetHeight,
      br.height,
    );
    maxBottomRel = Math.max(maxBottomRel, topRel + fullH);
  });
  const padBottom = parseFloat(window.getComputedStyle(rcEl).paddingBottom) || 0;
  return Math.ceil(maxBottomRel + padBottom);
}

/**
 * Prefer `.main-table-scroll-container` for LocationType / Historical tables (visible scroll rows).
 * Otherwise any table scroll container, then chart `.data-view-scroll-area`.
 * ReportCards: no viewport lock (full height).
 */
function getScrollPaneElementsForSnapshot(captureRoot) {
  if (!captureRoot?.querySelectorAll) return [];
  const ltHist = Array.from(captureRoot.querySelectorAll('.main-table-scroll-container')).filter(
    isLocationTypeOrHistoricalTableScrollContainer,
  );
  if (ltHist.length > 0) return ltHist;
  const anyTable = Array.from(captureRoot.querySelectorAll('.main-table-scroll-container'));
  if (anyTable.length > 0) return anyTable;
  const scrollAreas = Array.from(captureRoot.querySelectorAll('.data-view-scroll-area'));
  const hasReportCards = scrollAreas.some((el) => el.querySelector('.report-cards-container'));
  /** LocationNameScoresChart: full grid is taller than the viewport; do not lock scroll area to viewport. */
  const hasScoresGaugeChart = scrollAreas.some((el) => el.querySelector('.chart-container.scores-chart'));
  if (hasReportCards || hasScoresGaugeChart) return [];
  return scrollAreas;
}

function collectScrollSnapshot(captureRoot) {
  if (!captureRoot?.querySelectorAll) return [];
  return getScrollPaneElementsForSnapshot(captureRoot).map((el) => {
    const st = el.scrollTop;
    const sl = el.scrollLeft;
    const sh = el.scrollHeight;
    const ch = el.clientHeight;
    const sw = el.scrollWidth;
    const cw = el.clientWidth;
    const maxScrollY = Math.max(0, sh - ch);
    const maxScrollX = Math.max(0, sw - cw);
    return {
      scrollTop: st,
      scrollLeft: sl,
      /** 0–1: where the viewport sits in the scrollable range (stable when clone reflows at letter width). */
      scrollRatioY: maxScrollY > 0 ? st / maxScrollY : 0,
      scrollRatioX: maxScrollX > 0 ? sl / maxScrollX : 0,
      viewportHeightPx: measureScrollPaneViewportPx(el),
      clientWidth: el.clientWidth,
    };
  });
}

function applyScrollPositionToViewport(el, snap) {
  const maxY = Math.max(0, el.scrollHeight - el.clientHeight);
  const maxX = Math.max(0, el.scrollWidth - el.clientWidth);
  const clamp01 = (t) => Math.min(1, Math.max(0, t));
  if (typeof snap.scrollRatioY === 'number' && !Number.isNaN(snap.scrollRatioY) && maxY > 0) {
    el.scrollTop = clamp01(snap.scrollRatioY) * maxY;
  } else {
    el.scrollTop = snap.scrollTop;
  }
  if (typeof snap.scrollRatioX === 'number' && !Number.isNaN(snap.scrollRatioX) && maxX > 0) {
    el.scrollLeft = clamp01(snap.scrollRatioX) * maxX;
  } else {
    el.scrollLeft = snap.scrollLeft;
  }
}

function applyScrollViewportToClone(doc, scrollSnapshot, captureRootSelector = '.split-view-content') {
  if (!scrollSnapshot?.length) return;
  const captureRoot = doc.querySelector(captureRootSelector);
  const nodes = captureRoot ? getScrollPaneElementsForSnapshot(captureRoot) : [];
  nodes.forEach((el, i) => {
    const snap = scrollSnapshot[i];
    if (!snap) return;
    el.style.boxSizing = 'border-box';
    el.style.overflow = 'hidden';
    const hPx = snap.viewportHeightPx ?? snap.clientHeight;
    if (hPx > 0) {
      const h = `${hPx}px`;
      el.style.height = h;
      el.style.maxHeight = h;
      el.style.minHeight = h;
    }
    applyScrollPositionToViewport(el, snap);
  });
}

/** Sticky column / thead rules shared by full-page export and PNG viewport table capture. */
function applyTableRasterFlattenStylesInClone(doc) {
  const stickyCols =
    '.main-table th:first-child, .main-table td:first-child, ' +
    '.historical-table th:first-child, .historical-table td:first-child, ' +
    '.location-type-table th:first-child, .location-type-table td:first-child, ' +
    '.location-type-table th:nth-child(2), .location-type-table td:nth-child(2)';
  doc.querySelectorAll(stickyCols).forEach((el) => {
    el.style.boxShadow = 'none';
    el.style.position = 'static';
  });

  const firstColCells =
    '.main-table:not(.location-type-table) th:first-child, .main-table:not(.location-type-table) td:first-child, ' +
    '.historical-table th:first-child, .historical-table td:first-child';
  doc.querySelectorAll(firstColCells).forEach((el) => {
    el.style.borderRight = '1px solid var(--border-color)';
  });

  doc.querySelectorAll('.main-table thead').forEach((el) => {
    el.style.borderTop = 'none';
  });

  doc.querySelectorAll('.location-type-table thead th').forEach((th) => {
    const tr = th.parentElement;
    if (!tr || tr.tagName !== 'TR') return;
    const idx = Array.prototype.indexOf.call(tr.children, th);
    th.style.setProperty('border-left', 'none', 'important');
    th.style.setProperty('border-top', 'none', 'important');
    th.style.setProperty('border-right', idx === 1 ? '1px solid #e5e7eb' : 'none', 'important');
    th.style.setProperty('border-bottom', '2px solid #e5e7eb', 'important');
    th.style.setProperty('box-shadow', 'none', 'important');
    th.style.setProperty('outline', 'none', 'important');
    th.style.setProperty('background', 'transparent', 'important');
  });
}

// ─── onclone callback ─────────────────────────────────────────────────────────
function applyExportFixes(
  doc,
  liveLocationText,
  liveTitleStyle,
  reportCardsLayout = null,
  scrollSnapshot = null,
  exportFromMobileLayout = false,
  exportFullTableRaster = false,
  hideAnalysisHeaderInClone = false,
  footnoteNumbers = null,
) {
  injectExportDesktopStyles(doc);
  replaceFootnotesButtonsWithSpansInExportClone(doc);
  applyExportIconLogoDesktop(doc);

  // Always collapse inactive map + flex fixes when .split-view exists (html2canvas uses 1280px
  // viewport). Otherwise desktop exports keep a two-pane flex row; locking split-view to LETTER_PAGE_W
  // below would squeeze chart + map together. No-op if the clone has no .split-view (e.g. subtree root).
  applyMobileSplitViewWidthForExportClone(doc);

  if (hideAnalysisHeaderInClone) {
    doc.querySelectorAll('.analysis-header').forEach((el) => {
      el.style.display = 'none';
    });
  }

  // ── Remove chrome from layout flow (display:none) ──────────────────────────
  [
    '.analysis-tabs', '.view-toggle', '.table-controls', '.pagination-container',
    '.table-breadcrumb', '.export-actions', '.filters-container',
    '.filters-overlay', '.dropdown-menu-portal',
  ].forEach(sel =>
    doc.querySelectorAll(sel).forEach(el => { el.style.display = 'none'; })
  );

  // Location chevron + alert: handled in injectExportDesktopStyles (display:none) so layout centers.

  // Letter width — must use !important so it wins over applyMobileSplitViewWidthForExportClone flex
  // rules and any desktop 50% / 100% rules in bundled CSS (otherwise capture is ~1280px wide with a
  // narrow chart and large empty side bands).
  doc.querySelectorAll('.split-view').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('margin-left', '0', 'important');
    el.style.setProperty('margin-right', '0', 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
  });
  doc.querySelectorAll('.split-view-content').forEach((el) => {
    el.style.setProperty('width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('max-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('min-width', `${LETTER_PAGE_W}px`, 'important');
    el.style.setProperty('margin-left', '0', 'important');
    el.style.setProperty('margin-right', '0', 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
  });

  doc.querySelectorAll('.mobile-analysis-header').forEach((el) => {
    el.style.position = 'relative';
    el.style.top = 'auto';
    el.style.left = 'auto';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.zIndex = 'auto';
    el.style.width = `${LETTER_PAGE_W}px`;
    el.style.maxWidth = `${LETTER_PAGE_W}px`;
    el.style.minWidth = `${LETTER_PAGE_W}px`;
    el.style.marginLeft = '0';
    el.style.marginRight = '0';
    el.style.boxSizing = 'border-box';
  });
  doc.querySelectorAll('.mobile-analysis-header .analysis-header').forEach((el) => {
    el.style.position = 'relative';
    el.style.top = 'auto';
    el.style.left = 'auto';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.zIndex = 'auto';
    el.style.width = `${LETTER_PAGE_W}px`;
    el.style.maxWidth = `${LETTER_PAGE_W}px`;
    el.style.minWidth = `${LETTER_PAGE_W}px`;
    el.style.marginLeft = '0';
    el.style.marginRight = '0';
    el.style.boxSizing = 'border-box';
  });

  applyTableRasterFlattenStylesInClone(doc);

  if (scrollSnapshot?.length) {
    applyScrollViewportToClone(doc, scrollSnapshot);
  } else {
    doc.querySelectorAll('.data-view-scroll-area, .main-table-scroll-container').forEach((el) => {
      el.style.overflow = 'visible';
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
    });
  }

  if (exportFullTableRaster) {
    doc.querySelectorAll('.main-table-scroll-container').forEach((el) => {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    });
  }

  if (reportCardsLayout?.height > 0) {
    doc.querySelectorAll('.split-view-content[data-reports="true"]').forEach((el) => {
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('position', 'relative', 'important');
      el.style.setProperty('top', 'auto', 'important');
      el.style.setProperty('left', 'auto', 'important');
      el.style.setProperty('right', 'auto', 'important');
    });
    doc.querySelectorAll('.split-view-content .data-view-container').forEach((el) => {
      if (el.querySelector('.report-cards-container')) {
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('min-height', '0', 'important');
      }
    });
    doc.querySelectorAll('.split-view-content .data-view-content').forEach((el) => {
      if (el.querySelector('.report-cards-container')) {
        el.style.setProperty('flex', 'none', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('min-height', '0', 'important');
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('max-height', 'none', 'important');
      }
    });
    doc.querySelectorAll('.report-cards-container').forEach((node) => {
      node.style.setProperty('width', '100%', 'important');
      node.style.setProperty('max-width', '100%', 'important');
      node.style.removeProperty('min-width');
      node.style.setProperty('min-height', `${reportCardsLayout.height}px`, 'important');
      node.style.setProperty('height', 'auto', 'important');
      node.style.setProperty('max-height', 'none', 'important');
      node.style.setProperty('flex', 'none', 'important');
      node.style.setProperty('box-sizing', 'border-box', 'important');
    });
    doc.querySelectorAll('.split-view-content .data-view-scroll-area').forEach((el) => {
      if (el.querySelector('.report-cards-container')) {
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('max-height', 'none', 'important');
      }
    });
    doc.querySelectorAll('.report-card').forEach((el) => {
      el.style.setProperty('overflow', 'visible', 'important');
      el.style.setProperty('min-height', 'auto', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('contain', 'none', 'important');
    });
  }

  // Strip ALL React inline layout/sizing styles from the location title div
  // so the injected CSS !important rules take full effect.
  doc.querySelectorAll('.location-title-container .auto-resize-container > div').forEach((div) => {
    ['font-size', 'line-height', 'width', 'max-width', 'min-width',
     'white-space', 'word-wrap', 'word-break', 'overflow-wrap', 'hyphens',
     'display', 'text-align', 'transform',
    ].forEach((p) => div.style.removeProperty(p));
  });

  // Flatten comma + suffix to one text run in the clone; copy live typography +
  // punctuation spacing so html2canvas matches the screen (AnalysisHeader unchanged).
  // Strip layout/sizing values from the snapshot so the CSS inject controls them.
  if (liveLocationText) {
    const rasterText = normalizeLocationTextForExportRaster(liveLocationText);
    const cleanStyle = liveTitleStyle ? { ...liveTitleStyle } : null;
    if (cleanStyle) {
      delete cleanStyle.fontSize;
      delete cleanStyle.lineHeight;
      delete cleanStyle.width;
      delete cleanStyle.maxWidth;
      delete cleanStyle.minWidth;
      delete cleanStyle.whiteSpace;
      delete cleanStyle.wordBreak;
      delete cleanStyle.overflowWrap;
      delete cleanStyle.hyphens;
      delete cleanStyle.display;
      delete cleanStyle.transform;
      delete cleanStyle.textAlign;
    }
    doc.querySelectorAll('.location-title-container .auto-resize-container > div').forEach(div => {
      applyTitleSnapshotToClone(div, rasterText, cleanStyle, footnoteNumbers);
    });
  }
  // Title row: avoid clone inheriting mobile max-width / wrap constraints when viewport was narrow.
  doc.querySelectorAll('.location-title-container, .location-title-container .auto-resize-container').forEach((el) => {
    el.style.maxWidth = 'none';
    el.style.width = '100%';
  });

  doc.querySelectorAll('.analysis-header, .main-table thead tr, .main-table tbody tr').forEach(el => {
    el.style.webkitPrintColorAdjust = 'exact';
    el.style.printColorAdjust = 'exact';
  });

  // Charts: strip tooltip/cursor layers so extracts never show hover UI (clone has no pointer events).
  doc.querySelectorAll('.recharts-tooltip-wrapper').forEach((el) => el.remove());
  doc.querySelectorAll('.recharts-tooltip-cursor').forEach((el) => el.remove());
  doc.querySelectorAll('.chart-container .chart-content').forEach((el) => {
    el.style.pointerEvents = 'none';
  });
  applyExportChartLetterWidths(doc);
  syncRechartsSurfaceSvgWidths(doc);

  if (exportFullTableRaster) {
    const root = doc.querySelector('.split-view-content');
    pendingExportRowIntervalsCss = null;
    if (root) {
      const rr = root.getBoundingClientRect();
      const rows = Array.from(
        root.querySelectorAll('table.main-table thead tr, table.main-table tbody tr'),
      );
      if (rows.length) {
        pendingExportRowIntervalsCss = rows.map((tr) => {
          const r = tr.getBoundingClientRect();
          return {
            top: r.top - rr.top + root.scrollTop,
            bottom: r.bottom - rr.top + root.scrollTop,
          };
        });
      }
    }
  }
}

/**
 * Clone-only fixes for the off-screen mobile header capture (no live DOM mutation).
 */
function applyExportFixesForMobileHeaderClone(doc, liveLocationText, liveTitleStyle, footnoteNumbers = null) {
  injectExportDesktopStyles(doc);
  replaceFootnotesButtonsWithSpansInExportClone(doc);
  applyExportIconLogoDesktop(doc);

  doc.querySelectorAll('.location-title-container .auto-resize-container > div').forEach((div) => {
    ['font-size', 'line-height', 'width', 'max-width', 'min-width',
     'white-space', 'word-wrap', 'word-break', 'overflow-wrap', 'hyphens',
     'display', 'text-align', 'transform',
    ].forEach((p) => div.style.removeProperty(p));
  });

  if (liveLocationText) {
    const rasterText = normalizeLocationTextForExportRaster(liveLocationText);
    const cleanStyle = liveTitleStyle ? { ...liveTitleStyle } : null;
    if (cleanStyle) {
      delete cleanStyle.fontSize;
      delete cleanStyle.lineHeight;
      delete cleanStyle.width;
      delete cleanStyle.maxWidth;
      delete cleanStyle.minWidth;
      delete cleanStyle.whiteSpace;
      delete cleanStyle.wordBreak;
      delete cleanStyle.overflowWrap;
      delete cleanStyle.hyphens;
      delete cleanStyle.display;
      delete cleanStyle.transform;
      delete cleanStyle.textAlign;
    }
    doc.querySelectorAll('.location-title-container .auto-resize-container > div').forEach((div) => {
      applyTitleSnapshotToClone(div, rasterText, cleanStyle, footnoteNumbers);
    });
  }
  doc.querySelectorAll('.location-title-container, .location-title-container .auto-resize-container').forEach((el) => {
    el.style.maxWidth = 'none';
    el.style.width = '100%';
  });

  doc.querySelectorAll('.analysis-header').forEach((el) => {
    el.style.webkitPrintColorAdjust = 'exact';
    el.style.printColorAdjust = 'exact';
  });
}

/**
 * Height for html2canvas `windowHeight`.
 *
 * PNG: viewport capture on table scrollers (`measureTableScrollViewportPx`). PDF/print for data
 * tables (`fullTableRaster`): full `scrollHeight` for all rows in the current result set, then tiled
 * across letter pages — not shrunk to one page. Charts: client box + slack. Location Name scores
 * gauge adds `EXPORT_SCORES_GAUGE_EXTRA_PAD_PX` because clone letter-width reflow can exceed live
 * scroll metrics (desktop header-in-capture is most affected).
 *
 * Full-table: `scrollHeight` can match the viewport; row-sum + `measureFullTableRasterMinBottomPx`,
 * `EXPORT_FULL_TABLE_REFLOW_HEADROOM_MULT` (clone letter-width), and extra pads size `windowHeight`
 * so html2canvas does not clip. Mobile adds `EXPORT_FULL_TABLE_MOBILE_EXTRA_PAD_PX`.
 */
function measureCaptureRootHeight(captureRoot, options = {}) {
  const { fullTableRaster = false } = options;
  if (!captureRoot?.getBoundingClientRect) return 800;
  const scoresGaugeChart = !!captureRoot.querySelector?.('.chart-container.scores-chart');
  const rootRect = captureRoot.getBoundingClientRect();
  let maxBottom = 0;

  const tableScrolls = Array.from(captureRoot.querySelectorAll('.main-table-scroll-container'));
  if (tableScrolls.length > 0) {
    tableScrolls.forEach((node) => {
      const r = node.getBoundingClientRect();
      const topRel = r.top - rootRect.top;
      const h = fullTableRaster
        ? Math.ceil(Math.max(node.scrollHeight, node.clientHeight, r.height)) + EXPORT_VIEWPORT_SLACK_PX
        : measureScrollPaneViewportPx(node);
      maxBottom = Math.max(maxBottom, topRel + h);
    });
  } else {
    const scrollAreas = Array.from(captureRoot.querySelectorAll('.data-view-scroll-area'));
    const hasReports = scrollAreas.some((el) => el.querySelector('.report-cards-container'));
    scrollAreas.forEach((node) => {
      const r = node.getBoundingClientRect();
      const topRel = r.top - rootRect.top;
      const rc = node.querySelector('.report-cards-container');
      const scoresChart = node.querySelector('.chart-container.scores-chart');
      let h;
      if (hasReports && rc) {
        h = measureReportCardsContentHeight(rc) + EXPORT_VIEWPORT_SLACK_PX;
      } else if (hasReports) {
        h = Math.ceil(Math.max(node.scrollHeight, node.clientHeight, r.height)) + EXPORT_VIEWPORT_SLACK_PX;
      } else if (scoresChart) {
        const scrollH = Math.ceil(
          Math.max(
            node.scrollHeight,
            scoresChart.scrollHeight,
            scoresChart.offsetHeight,
            r.height,
          ),
        ) + EXPORT_VIEWPORT_SLACK_PX;
        const chartBottomRel = Math.ceil(scoresChart.getBoundingClientRect().bottom - rootRect.top);
        h = Math.max(scrollH, chartBottomRel - topRel + EXPORT_VIEWPORT_SLACK_PX);
      } else {
        h = measureScrollPaneViewportPx(node);
      }
      maxBottom = Math.max(maxBottom, topRel + h);
    });
  }

  captureRoot.querySelectorAll('.report-cards-container').forEach((node) => {
    const r = node.getBoundingClientRect();
    const topRel = r.top - rootRect.top;
    const visibleH = measureReportCardsContentHeight(node) + EXPORT_VIEWPORT_SLACK_PX;
    maxBottom = Math.max(maxBottom, topRel + visibleH);
  });

  const fallback = fullTableRaster
    ? Math.max(captureRoot.scrollHeight, captureRoot.offsetHeight, Math.ceil(rootRect.height))
    : Math.max(captureRoot.offsetHeight, Math.ceil(rootRect.height));

  const rowMinBottom = fullTableRaster ? measureFullTableRasterMinBottomPx(captureRoot) : 0;
  let combined = Math.max(maxBottom, fallback, rowMinBottom);
  if (fullTableRaster) {
    combined = Math.ceil(combined * EXPORT_FULL_TABLE_REFLOW_HEADROOM_MULT);
  }

  const hasReportCards = !!captureRoot.querySelector('.report-cards-container');
  const pad =
    EXPORT_HTML2CANVAS_WINDOW_PAD_PX
    + (fullTableRaster ? EXPORT_FULL_TABLE_RASTER_EXTRA_PX : 0)
    + (scoresGaugeChart ? EXPORT_SCORES_GAUGE_EXTRA_PAD_PX : 0)
    + (fullTableRaster && isMobileAnalysisLayoutActiveInLiveDom() ? EXPORT_FULL_TABLE_MOBILE_EXTRA_PAD_PX : 0)
    + (hasReportCards ? EXPORT_REPORTS_CAPTURE_HEADROOM_PX : 0);
  return Math.max(
    800,
    Math.ceil(combined + pad),
  );
}

// ─── html2canvas wrapper ──────────────────────────────────────────────────────
/**
 * @param {string | null | undefined} canonicalLocationName - Same string as CSV titles / formatExportLocationDisplay
 *   (state abbreviations for city & county). Prefer over DOM innerText so mobile line breaks / narrow reflow cannot change hyphens and spacing.
 */
async function captureElement(el, scale = 1.5, canonicalLocationName = null, options = {}) {
  const { fullTableRaster = false, hideAnalysisHeaderInClone = false } = options;
  const liveTextDiv = el.querySelector('.location-title-container .auto-resize-container > div');
  let liveLocationText = null;
  let liveTitleStyle = null;
  const canonical = canonicalLocationName != null && String(canonicalLocationName).trim()
    ? String(canonicalLocationName).trim()
    : null;
  if (liveTextDiv) {
    liveTitleStyle = pickTitleSnapshotStyle(window.getComputedStyle(liveTextDiv));
    liveLocationText = canonical || liveTextDiv.innerText.replace(/\s+/g, ' ').trim();
  } else if (canonical) {
    liveLocationText = canonical;
  }

  let footnoteNumbersForRaster = options.footnoteNumbers;
  if (footnoteNumbersForRaster == null && liveTextDiv) {
    footnoteNumbersForRaster = readFootnoteNumbersFromLiveTitleDiv(liveTextDiv);
  }

  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  let reportCardsLayout = null;
  const rcEl = el.querySelector('.report-cards-container');
  if (rcEl) {
    const fullH = measureReportCardsContentHeight(rcEl);
    if (fullH > 0) {
      reportCardsLayout = { height: Math.round(fullH) };
    }
  }

  const scrollSnapshot = fullTableRaster ? null : collectScrollSnapshot(el);
  const exportFromMobileLayout = isMobileAnalysisLayoutActiveInLiveDom();

  const { default: html2canvas } = await import('html2canvas');
  const approxH = measureCaptureRootHeight(el, { fullTableRaster });
  if (fullTableRaster) pendingExportRowIntervalsCss = null;
  const canvas = await html2canvas(el, {
    scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: EXPORT_LAYOUT_VIEWPORT_W,
    windowHeight: approxH,
    onclone: (clonedDoc) =>
      applyExportFixes(
        clonedDoc,
        liveLocationText,
        liveTitleStyle,
        reportCardsLayout,
        scrollSnapshot,
        exportFromMobileLayout,
        fullTableRaster,
        hideAnalysisHeaderInClone,
        footnoteNumbersForRaster,
      ),
  });
  const rowIntervalsCss = fullTableRaster ? pendingExportRowIntervalsCss : null;
  pendingExportRowIntervalsCss = null;
  return { canvas, rowIntervalsCss };
}

// ─── Trim trailing white rows from bottom of canvas ───────────────────────────
// .split-view-content is sized to the full viewport height. Trimming removes
// the blank canvas area below the last content row.
function trimCanvasBottom(canvas, scaledPad = 24) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;

  const isContentPixel = (i) => {
    const a = data[i + 3];
    if (a <= 20) return false;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    return r < 249 || g < 249 || b < 249;
  };

  let lastRow = 0;
  outer: for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (isContentPixel(i)) {
        lastRow = y;
        break outer;
      }
    }
  }

  const newH = Math.min(lastRow + scaledPad, height);
  if (newH >= height) return canvas;

  const out = document.createElement('canvas');
  out.width = width; out.height = newH;
  const oc = out.getContext('2d');
  oc.fillStyle = '#ffffff';
  oc.fillRect(0, 0, width, newH);
  oc.drawImage(canvas, 0, 0);
  return out;
}

/** Keep pixels [0, heightPx); used to drop html2canvas slack below the last table row without pixel heuristics. */
function cropCanvasToTopHeight(canvas, heightPx) {
  const h = Math.min(canvas.height, Math.max(1, Math.round(heightPx)));
  if (h >= canvas.height) return canvas;
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, h);
  ctx.drawImage(canvas, 0, 0);
  return out;
}

/**
 * Full-table PDF/print: windowHeight is intentionally large so rows are not clipped; crop the raster
 * to the clone-measured bottom of the last header/data row (same geometry as rowIntervals for tiling).
 * Falls back to trimCanvasBottom when row intervals are missing.
 */
function trimFullTableRasterContentCanvas(content, rowIntervalsCss, scale, headerOffsetPx, padPx) {
  const slackBelow = Math.round(Math.max(EXPORT_VIEWPORT_SLACK_PX, 8) * scale);
  if (rowIntervalsCss?.length) {
    const last = rowIntervalsCss[rowIntervalsCss.length - 1];
    const bottomCss = Math.max(last.top, last.bottom);
    const targetPx = Math.ceil(headerOffsetPx + bottomCss * scale + slackBelow);
    if (targetPx > 0 && targetPx < content.height) {
      return cropCanvasToTopHeight(content, targetPx);
    }
    return content;
  }
  return trimCanvasBottom(content, padPx);
}

// ─── Source-footer canvas ─────────────────────────────────────────────────────
// Italic, 1px smaller than .main-table-top (14px → 13px at scale 1).
function breakLongToken(ctx, token, maxW) {
  const out = [];
  let rest = token;
  while (rest.length) {
    let lo = 1;
    let hi = rest.length;
    let best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (ctx.measureText(rest.slice(0, mid)).width <= maxW) { best = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    if (best < 1) best = 1;
    out.push(rest.slice(0, best));
    rest = rest.slice(best);
  }
  return out;
}

function wrapTextLines(ctx, text, maxW) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const chunks = ctx.measureText(w).width <= maxW ? [w] : breakLongToken(ctx, w, maxW);
    for (const part of chunks) {
      const test = line ? `${line} ${part}` : part;
      if (ctx.measureText(test).width <= maxW) line = test;
      else {
        if (line) lines.push(line);
        line = part;
      }
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

/** `URL: https://…` — keeps the `URL: ` prefix on the same line as the start of the href (no orphan `URL:`). */
function wrapUrlFooterLines(ctx, href, maxW) {
  const prefix = 'URL: ';
  const full = prefix + href;
  if (ctx.measureText(full).width <= maxW) return [full];
  const prefixW = ctx.measureText(prefix).width;
  const firstHrefW = maxW - prefixW;
  if (firstHrefW <= 0) {
    return wrapTextLines(ctx, full, maxW);
  }
  const lines = [];
  let rest = href;
  const firstSeg = breakLongToken(ctx, rest, firstHrefW)[0];
  lines.push(prefix + firstSeg);
  rest = rest.slice(firstSeg.length);
  while (rest.length) {
    const seg = breakLongToken(ctx, rest, maxW)[0];
    lines.push(seg);
    rest = rest.slice(seg.length);
  }
  return lines.length ? lines : [full];
}

const SOURCE_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function buildSourceCanvas(width, scale = 1.5, sourceUrl, options = {}) {
  const { gapTopPx, dataSourceLabel: dataSourceLabelOpt } = options;
  const fontSize = Math.round(SOURCE_FOOTER_FONT_PX * scale);
  const lineH    = Math.round(fontSize * 1.35);
  const gapTop   = gapTopPx != null ? gapTopPx : Math.round(20 * scale);
  const xPad     = Math.round(SOURCE_X_PAD_PX * scale);
  const padBottom = Math.round(8 * scale);
  const maxW       = width - xPad * 2;
  const gapBeforeCopyright = Math.round(20 * scale);

  const href = sourceUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const label = dataSourceLabelOpt ?? getExportDataSourceDisplayName(undefined);
  const dataLine = `Data Source: ${label}`;
  const probe = document.createElement('canvas').getContext('2d');
  const fontSpec = `italic ${fontSize}px ${SOURCE_FONT_STACK}`;
  probe.font = fontSpec;
  const lines = [
    ...wrapTextLines(probe, dataLine, maxW),
    ...wrapUrlFooterLines(probe, href, maxW),
  ];

  const copyrightFontSize = Math.max(1, Math.round(COPYRIGHT_FOOTER_FONT_PX * scale));
  const copyrightLineH = Math.round(copyrightFontSize * 1.35);
  const copyrightFontSpec = `${copyrightFontSize}px ${SOURCE_FONT_STACK}`;
  const copyrightText = getKrimeExportCopyrightLine();
  probe.font = copyrightFontSpec;
  const copyLines = wrapTextLines(probe, copyrightText, maxW);

  const H = gapTop + lines.length * lineH + gapBeforeCopyright + copyLines.length * copyrightLineH + padBottom;
  const c = document.createElement('canvas');
  c.width = width; c.height = H;
  const ctx = c.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, H);

  ctx.fillStyle = '#000000';
  ctx.font = fontSpec;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  let y = gapTop;
  for (const ln of lines) {
    ctx.fillText(ln, xPad, y);
    y += lineH;
  }

  y += gapBeforeCopyright;
  ctx.font = copyrightFontSpec;
  ctx.textAlign = 'center';
  const cx = width / 2;
  for (const ln of copyLines) {
    ctx.fillText(ln, cx, y);
    y += copyrightLineH;
  }
  return c;
}

/** Score methodology line — same px as source footer; not italic (matches export text preference). */
function buildScoreFootnoteCanvas(width, scale, text, options = {}) {
  const { gapTopPx } = options;
  const t = text != null ? String(text).trim() : '';
  if (!t) return null;
  const fontSize = Math.round(SOURCE_FOOTER_FONT_PX * scale);
  const lineH = Math.round(fontSize * 1.35);
  const gapTop = gapTopPx != null ? gapTopPx : Math.round(10 * scale);
  const xPad = Math.round(SOURCE_X_PAD_PX * scale);
  const padBottom = Math.round(8 * scale);
  const maxW = width - xPad * 2;
  const probe = document.createElement('canvas').getContext('2d');
  const fontSpec = `${fontSize}px ${SOURCE_FONT_STACK}`;
  probe.font = fontSpec;
  const lines = wrapTextLines(probe, t, maxW);
  const H = gapTop + lines.length * lineH + padBottom;
  const c = document.createElement('canvas');
  c.width = width;
  c.height = H;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, H);
  ctx.fillStyle = '#000000';
  ctx.font = fontSpec;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let y = gapTop;
  for (const ln of lines) {
    ctx.fillText(ln, xPad, y);
    y += lineH;
  }
  return c;
}

/**
 * Location footnotes: superscript-sized index + body text (narrow gap). Placed above score methodology
 * and source on raster exports.
 */
function buildLocationFootnotesCanvas(width, scale, footnotesMap, options = {}) {
  const { gapTopPx } = options;
  if (!footnotesMap || !Object.keys(footnotesMap).length) return null;
  const entries = Object.entries(footnotesMap).sort((a, b) => {
    const na = parseInt(a[0].replace('footnote_', ''), 10);
    const nb = parseInt(b[0].replace('footnote_', ''), 10);
    return na - nb;
  });
  const bodyFontSize = Math.round(SOURCE_FOOTER_FONT_PX * scale);
  const supNumSize = Math.max(8, Math.round(bodyFontSize * 0.52));
  const lineH = Math.round(bodyFontSize * 1.35);
  const supRise = Math.round(supNumSize * 0.38);
  const gapTop = gapTopPx != null ? gapTopPx : Math.round(10 * scale);
  const gapItem = Math.round(8 * scale);
  const padBottom = Math.round(8 * scale);
  const xPad = Math.round(SOURCE_X_PAD_PX * scale);
  const textGap = Math.round(2 * scale);
  const probe = document.createElement('canvas').getContext('2d');
  const numFontSpec = `600 ${supNumSize}px ${SOURCE_FONT_STACK}`;
  const bodyFontSpec = `${bodyFontSize}px ${SOURCE_FONT_STACK}`;

  const prelim = [];
  for (const [key, html] of entries) {
    const num = key.replace('footnote_', '');
    const plain = stripHtmlToPlainTextForExport(html);
    const paras = plain.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    if (!paras.length) continue;
    prelim.push({ num, paras });
  }
  if (!prelim.length) return null;

  probe.font = numFontSpec;
  let maxNumW = 0;
  for (const p of prelim) {
    maxNumW = Math.max(maxNumW, probe.measureText(String(p.num)).width);
  }
  const numColW = Math.max(Math.round(16 * scale), Math.ceil(maxNumW) + Math.round(4 * scale));
  const maxTextW = Math.max(40, width - 2 * xPad - numColW - textGap);

  probe.font = bodyFontSpec;
  const blocks = [];
  for (const { num, paras } of prelim) {
    const lines = [];
    for (const para of paras) {
      lines.push(...wrapTextLines(probe, para, maxTextW));
    }
    blocks.push({ num, lines });
  }
  if (!blocks.length) return null;

  const topPad = supRise;
  let totalH = gapTop + topPad + padBottom;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    totalH += lineH * Math.max(1, b.lines.length);
    if (i < blocks.length - 1) totalH += gapItem;
  }

  const c = document.createElement('canvas');
  c.width = width;
  c.height = Math.ceil(totalH);
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, c.height);
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  let y = gapTop + topPad;
  const xText = xPad + numColW + textGap;
  for (let bi = 0; bi < blocks.length; bi++) {
    const b = blocks[bi];
    let yy = y;
    for (let li = 0; li < b.lines.length; li++) {
      if (li === 0) {
        ctx.font = numFontSpec;
        ctx.fillText(b.num, xPad, yy - supRise);
      }
      ctx.font = bodyFontSpec;
      ctx.fillText(b.lines[li], xText, yy);
      yy += lineH;
    }
    y = yy;
    if (bi < blocks.length - 1) y += gapItem;
  }
  return c;
}

/** Same wording as Pagination / CSV (`X to Y of Z results`), centered below the table on full-table PDF/print. */
function buildPaginationFooterCanvas(width, scale, paginationLineParts, fallbackText) {
  const t = fallbackText != null ? String(fallbackText).trim() : '';
  const parts = paginationLineParts && paginationLineParts.totalFormatted != null
    ? paginationLineParts
    : null;
  if (!parts && !t) return null;
  const fontSize = Math.round(MAIN_TABLE_TOP_FONT_PX * scale);
  const lineH = Math.round(fontSize * 1.35);
  const gapAfterTable = Math.round(20 * scale);
  const padBottom = 0;
  const fontSpec = `${fontSize}px ${SOURCE_FONT_STACK}`;
  const boldSpec = `bold ${fontSize}px ${SOURCE_FONT_STACK}`;
  const H = gapAfterTable + lineH + padBottom;
  const c = document.createElement('canvas');
  c.width = width;
  c.height = H;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, H);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  if (parts) {
    const prefix = `${parts.startItem} to ${parts.endItem} of `;
    const mid = String(parts.totalFormatted);
    const suffix = ` ${parts.noun}`;
    ctx.font = fontSpec;
    const w1 = ctx.measureText(prefix).width;
    ctx.font = boldSpec;
    const w2 = ctx.measureText(mid).width;
    ctx.font = fontSpec;
    const w3 = ctx.measureText(suffix).width;
    ctx.textAlign = 'left';
    let x = (width - (w1 + w2 + w3)) / 2;
    const y = gapAfterTable;
    ctx.font = fontSpec;
    ctx.fillText(prefix, x, y);
    x += w1;
    ctx.font = boldSpec;
    ctx.fillText(mid, x, y);
    x += w2;
    ctx.font = fontSpec;
    ctx.fillText(suffix, x, y);
  } else {
    ctx.font = fontSpec;
    ctx.textAlign = 'center';
    ctx.fillText(t, width / 2, gapAfterTable);
  }
  return c;
}

// ─── Stack canvases vertically ────────────────────────────────────────────────
function stackVertical(canvases) {
  const w = Math.max(...canvases.map(c => c.width));
  const h = canvases.reduce((s, c) => s + c.height, 0);
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  let y = 0;
  for (const c of canvases) {
    if (c.width < w) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, y, w, c.height);
    }
    ctx.drawImage(c, 0, y);
    y += c.height;
  }
  return out;
}

/**
 * html2canvas width varies slightly (subpixel layout, mobile stacked max vs single capture).
 * `fitCanvasToLetterPortrait` uses uniform scale s = min(PAGE_W/sw, PAGE_H/sh); different sw
 * changes s and the letterboxed content width on the page. Fix sw to LETTER_PAGE_W * scale.
 */
function padOrCropCanvasToLetterRasterWidth(canvas, html2canvasScale) {
  const targetW = Math.round(LETTER_PAGE_W * html2canvasScale);
  const w = canvas.width;
  const h = canvas.height;
  if (!w || !h || w === targetW) return canvas;
  const out = document.createElement('canvas');
  out.width = targetW;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetW, h);
  if (w < targetW) {
    ctx.drawImage(canvas, Math.round((targetW - w) / 2), 0);
  } else {
    const sx = Math.floor((w - targetW) / 2);
    ctx.drawImage(canvas, sx, 0, targetW, h, 0, 0, targetW, h);
  }
  return out;
}

/**
 * Avoid cutting through table rows, pagination strip, or methodology+source when tiling full-table PDF/print.
 * sliceMeta (canvas px): tableEndY, footerBlockStartY (methodology or source), paginationEndY, sourceStartY?, ih.
 * Pagination [tableEndY, paginationEndY) is atomic; methodology+source [footerBlockStartY, ih) is atomic.
 * Legacy (no footerBlockStartY key): merge entire tail from footerStartY (table end) as before.
 */
function snapFullTableSliceEnd(srcY, candidateEnd, ih, contentH, destHTotal, sliceMeta) {
  const eps = 0.5;
  let end = Math.min(candidateEnd, ih);
  if (!sliceMeta) {
    if (end <= srcY) end = Math.min(srcY + 1, ih);
    return Math.min(end, ih);
  }

  const {
    rowIntervals = [],
    tableEndY: TY = null,
    footerStartY: legacyF0 = null,
    footerBlockStartY: FBRaw = null,
    paginationEndY: PEndRaw = null,
    sourceStartY: SS = null,
  } = sliceMeta;

  const T = TY ?? legacyF0 ?? 0;
  const PEnd = PEndRaw != null ? PEndRaw : T;
  const legacyMode = !('footerBlockStartY' in sliceMeta) && legacyF0 != null;

  const segPt = (a, b) => ((b - a) / ih) * destHTotal;
  const F1 = ih;

  if (legacyMode) {
    const F0 = legacyF0;
    const hasPaginationStrip = PEndRaw != null && F0 != null && PEnd > F0;
    if (F0 != null && F0 > 0 && F0 < F1) {
      if (srcY < F0 && end > F0) {
        if (segPt(srcY, F0) + segPt(F0, F1) <= contentH + eps) end = F1;
        else end = F0;
      }
      if (end > F0 && end < F1) {
        if (srcY < F0) {
          end = F0;
        } else if (hasPaginationStrip && srcY < PEnd && end > PEnd) {
          if (segPt(srcY, PEnd) + segPt(PEnd, F1) <= contentH + eps) end = F1;
          else end = PEnd;
        } else if (srcY >= F0 && end < F1) {
          if (segPt(srcY, F1) <= contentH + eps) end = F1;
          else if (hasPaginationStrip && srcY < PEnd) end = PEnd;
          else end = F1;
        }
      }
    }
  } else if (FBRaw != null && FBRaw < F1) {
    const FB = FBRaw;
    if (srcY < FB && end > FB) {
      if (segPt(srcY, FB) + segPt(FB, F1) <= contentH + eps) end = F1;
      else end = FB;
    }
    if (end > FB && end < F1) {
      if (srcY < FB) {
        end = FB;
      } else if (SS != null && srcY < SS && end > SS) {
        if (segPt(srcY, SS) + segPt(SS, F1) <= contentH + eps) end = F1;
        else end = SS;
      } else if (srcY >= FB && end < F1) {
        if (segPt(srcY, F1) <= contentH + eps) end = F1;
        else if (SS != null && srcY < SS) end = SS;
        else end = F1;
      }
    }
  }

  const limit = TY != null ? Math.min(TY, ih) : ih;
  for (const { y0, y1 } of rowIntervals) {
    if (y1 <= srcY || y0 >= limit) continue;
    const ce = Math.min(end, limit);
    if (ce > y0 + eps && ce < y1 - eps && ce > srcY) {
      end = ce - y0 <= y1 - ce ? y0 : y1;
    }
  }

  if (!legacyMode && T != null && PEnd > T + eps) {
    if (end > T && end < PEnd) {
      if (srcY + eps < T) {
        end = T;
      } else if (srcY >= T - eps && srcY < PEnd) {
        if (segPt(srcY, PEnd) <= contentH + eps) end = PEnd;
        else end = T;
      }
    }
  }

  if (end <= srcY) end = Math.min(srcY + 1, ih);
  return Math.min(end, ih);
}

function sliceHeightPt(srcY, end, ih, destHTotal) {
  return ((end - srcY) / ih) * destHTotal;
}

/**
 * Row snap can push a slice past one printable page in pt. Browsers then split a single <img> across
 * pages (mid-row); jsPDF places one image per page. Clamp so each slice never exceeds contentH in pt.
 */
function clampSliceEndToMaxPageHeight(srcY, end, ih, contentH, destHTotal, rowIntervals) {
  const eps = 0.5;
  const rows = rowIntervals || [];
  const maxEndPx = Math.min(ih, srcY + (contentH / destHTotal) * ih);
  let e = Math.min(end, maxEndPx);
  for (const { y0, y1 } of rows) {
    if (e > y0 + eps && e < y1 - eps && e > srcY) {
      if (y0 > srcY + eps) e = Math.min(e, y0);
    }
  }
  if (e <= srcY) e = Math.min(srcY + 1, ih);
  return Math.min(e, ih);
}

/**
 * After tiling, if the footer block (pagination + footnotes + methodology + source)
 * ended up alone on a trailing page, merge it onto the last table page when
 * the combined height fits. If the table + footer together exceed one page,
 * the tiled drawSlice already scales oversized slices to fit.
 */
function mergeFooterRangesWhenNoRowSplit(ranges, sliceMeta) {
  if (!ranges || ranges.length < 2 || !sliceMeta) return ranges;

  const ih = sliceMeta.ih;
  const tableEndY = sliceMeta.tableEndY ?? ih;
  const footerBlockStartY = sliceMeta.footerBlockStartY ?? sliceMeta.footerStartY ?? tableEndY;

  if (footerBlockStartY >= ih - 0.5) return ranges;

  const eps = 0.5;

  // Find the first slice that is entirely footer (starts at or after the table end)
  const firstFooterOnlyIdx = ranges.findIndex((r) => r.srcY >= tableEndY - eps);
  if (firstFooterOnlyIdx <= 0) return ranges;

  // Merge everything from the last table slice through the end
  const lastTableSliceIdx = firstFooterOnlyIdx - 1;
  const lastTableSlice = ranges[lastTableSliceIdx];
  const mergedSrcY = lastTableSlice.srcY;
  const mergedSrcH = ih - mergedSrcY;

  // Always merge — the footer should never be orphaned on its own page.
  // If the merged slice exceeds one page height, embedCanvasInPDFTiled's
  // drawSlice and buildPrintSlicesFromCanvas's pushSlice both already
  // scale oversized slices to fit within one page.
  return [
    ...ranges.slice(0, lastTableSliceIdx),
    { srcY: mergedSrcY, srcH: mergedSrcH },
  ];
}

function buildFullTableTiledSliceRanges(ih, contentH, destHTotal, sliceMeta) {
  const ranges = [];
  let srcY = 0;
  let guard = 0;
  const rowIntervals = sliceMeta?.rowIntervals || [];
  while (srcY < ih - 0.001) {
    guard += 1;
    if (guard > 600) break;
    const remainingPx = ih - srcY;
    const remainingPt = (remainingPx / ih) * destHTotal;
    const sliceHPt = Math.min(contentH, remainingPt);
    const desiredSrcH = (sliceHPt / destHTotal) * ih;
    let candidateEnd = Math.min(srcY + desiredSrcH, ih);
    let end = snapFullTableSliceEnd(srcY, candidateEnd, ih, contentH, destHTotal, sliceMeta);
    const eps = 0.5;
    const tableEndY = sliceMeta?.tableEndY;
    if (sliceMeta && srcY < ih - 0.001) {
      const remainingPt = (remainingPx / ih) * destHTotal;
      if (remainingPt <= contentH + eps) {
        end = ih;
      } else if (
        tableEndY != null
        && tableEndY > 0
        && tableEndY < ih
        && srcY < tableEndY - eps
      ) {
        const toTableEndPt = ((tableEndY - srcY) / ih) * destHTotal;
        const tablePlusFooterPt = remainingPt;
        if (end > tableEndY + eps && toTableEndPt <= contentH + eps && tablePlusFooterPt > contentH + eps) {
          end = tableEndY;
        }
      }
    }
    if (sliceHeightPt(srcY, end, ih, destHTotal) > contentH + 0.5) {
      end = clampSliceEndToMaxPageHeight(srcY, end, ih, contentH, destHTotal, rowIntervals);
    }
    let srcH = Math.min(remainingPx, Math.max(1, end - srcY));
    if (srcH < 1 && remainingPx > 0) srcH = remainingPx;
    ranges.push({ srcY, srcH });
    srcY += srcH;
  }
  return ranges;
}

/** Letter portrait: uniform scale to fit inside page (no stretch). Optional centering (vertical and/or horizontal). */
function fitCanvasToLetterPortrait(src, pixelScale = 1, options = {}) {
  const { center = false, centerHorizontal = true } = options;
  const PAGE_W = Math.round(LETTER_PAGE_W * pixelScale);
  const PAGE_H = Math.round(LETTER_PAGE_H * pixelScale);
  const out = document.createElement('canvas');
  out.width = PAGE_W;
  out.height = PAGE_H;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);
  const sw = src.width;
  const sh = src.height;
  if (!sw || !sh) return out;
  const s = Math.min(PAGE_W / sw, PAGE_H / sh);
  const dw = sw * s;
  const dh = sh * s;
  const x = center && centerHorizontal ? (PAGE_W - dw) / 2 : 0;
  const y = center ? (PAGE_H - dh) / 2 : 0;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, sw, sh, x, y, dw, dh);
  return out;
}

function shouldCenterLocationNameScoresChartExtract(exportContext) {
  const tab = exportContext?.activeTab;
  const isScoresTab = tab === 'scores' || tab === 'score';
  return (
    exportContext?.selectedView === 'chart' &&
    isScoresTab &&
    exportContext?.view === 'locationName'
  );
}

// ─── Embed canvas: one US Letter portrait page (uniform scale, letterboxed — no stretch) ─
async function embedCanvasInPDF(canvas, filename) {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
    compress: true,
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const m = EXPORT_PDF_PRINT_MARGIN_PT;
  const contentW = pageW - 2 * m;
  const contentH = pageH - 2 * m;
  const iw = canvas.width;
  const ih = canvas.height;
  if (!iw || !ih) {
    doc.save(`${filename}.pdf`);
    return;
  }
  const s = Math.min(contentW / iw, contentH / ih);
  const dw = iw * s;
  const dh = ih * s;
  const x = m + (contentW - dw) / 2;
  const y = m + (contentH - dh) / 2;
  doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, dw, dh);
  doc.save(`${filename}.pdf`);
}

/** Full-height table raster: width fits printable area, height tiles across pages (no shrink-to-fit). */
async function embedCanvasInPDFTiled(canvas, filename, sliceMetaOverride = null) {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
    compress: true,
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const m = EXPORT_PDF_PRINT_MARGIN_PT;
  const contentW = pageW - 2 * m;
  const contentH = pageH - 2 * m;
  const iw = canvas.width;
  const ih = canvas.height;
  if (!iw || !ih) {
    doc.save(`${filename}.pdf`);
    return;
  }
  const destHTotal = (ih / iw) * contentW;
  const sliceMeta = sliceMetaOverride ?? canvas.__krimeSliceMeta ?? null;
  let ranges = sliceMeta
    ? buildFullTableTiledSliceRanges(ih, contentH, destHTotal, sliceMeta)
    : null;
  if (ranges?.length && sliceMeta) {
    ranges = mergeFooterRangesWhenNoRowSplit(ranges, sliceMeta);
  }
  let pageIndex = 0;
  const drawSlice = (srcY, srcH) => {
    const sliceHPt = (srcH / ih) * destHTotal;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = iw;
    sliceCanvas.height = Math.max(1, Math.ceil(srcH));
    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, srcY, iw, srcH, 0, 0, iw, srcH);
    const dataUrl = sliceCanvas.toDataURL('image/png');
    let dw = contentW;
    let dh = sliceHPt;
    let scaledToFitPage = false;
    if (dh > contentH + 0.5) {
      const s = Math.min(1, contentH / sliceHPt);
      dw = contentW * s;
      dh = sliceHPt * s;
      scaledToFitPage = true;
    }
    if (scaledToFitPage) {
      const x = m + (contentW - dw) / 2;
      const y = m + (contentH - dh) / 2;
      doc.addImage(dataUrl, 'PNG', x, y, dw, dh);
    } else {
      doc.addImage(dataUrl, 'PNG', m, m, contentW, sliceHPt);
    }
  };
  if (ranges && ranges.length) {
    for (const { srcY, srcH } of ranges) {
      if (pageIndex > 0) doc.addPage();
      drawSlice(srcY, srcH);
      pageIndex += 1;
      if (pageIndex > 500) break;
    }
  } else {
    let srcY = 0;
    while (srcY < ih - 0.001) {
      if (pageIndex > 0) doc.addPage();
      const remainingPx = ih - srcY;
      const remainingPt = (remainingPx / ih) * destHTotal;
      const sliceHPt = Math.min(contentH, remainingPt);
      let srcH = (sliceHPt / destHTotal) * ih;
      if (srcY + srcH > ih) srcH = remainingPx;
      srcH = Math.min(remainingPx, Math.max(1, srcH));
      if (srcH < 1 && remainingPx > 0) srcH = remainingPx;
      drawSlice(srcY, srcH);
      srcY += srcH;
      pageIndex += 1;
      if (pageIndex > 500) break;
    }
  }
  doc.save(`${filename}.pdf`);
}

// ─── CSV ─────────────────────────────────────────────────────────────────────
export function downloadCSV(exportContext) {
  const {
    filename,
    headers,
    rows,
    title,
    sortedByLine,
    mainTableTopHeading,
    paginationSummary,
    scoreFootnote,
    locationFootnotes,
  } = extractTableData(exportContext);
  if (!headers.length) return;
  const esc = (v) => { const s = String(v ?? ''); return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s; };
  const sourceUrl = buildExportSourceUrl(exportContext);
  const dataSourceLabel = getExportDataSourceDisplayName(exportContext?.selectedSource);
  const isReportsExport = exportContext.activeTab === 'reports';
  const footnotesBlock = formatFootnotesBlockForTextExport(locationFootnotes);
  const parts = [esc(title), ''];
  if (sortedByLine) {
    parts.push(esc(sortedByLine), '');
  }
  if (mainTableTopHeading) {
    parts.push(esc(mainTableTopHeading), '');
  }
  parts.push(
    headers.map(esc).join(','),
    ...(isReportsExport ? [''] : []),
    ...rows.map((r) => r.map(esc).join(',')),
    '',
  );
  if (paginationSummary) {
    parts.push(esc(paginationSummary), '');
  }
  if (footnotesBlock) {
    footnotesBlock.split('\n').forEach((line) => {
      parts.push(esc(line));
    });
    parts.push('');
  }
  if (scoreFootnote) {
    parts.push(esc(scoreFootnote), '');
  }
  parts.push(`Data Source: ${dataSourceLabel}`, `URL: ${sourceUrl}`, '', getKrimeExportCopyrightLine());
  // UTF-8 BOM so Excel (Windows) opens the file as UTF-8; otherwise ™ and other non-ASCII show as garbled.
  triggerDownload(parts.join('\r\n'), `${filename}.csv`, 'text/csv;charset=utf-8;', { prependUtf8Bom: true });
}

// ─── Text ─────────────────────────────────────────────────────────────────────
export function downloadText(exportContext) {
  const {
    filename,
    headers,
    rows,
    title,
    sortedByLine,
    mainTableTopHeading,
    paginationSummary,
    scoreFootnote,
    locationFootnotes,
  } = extractTableData(exportContext);
  if (!headers.length) return;
  const sourceUrl = buildExportSourceUrl(exportContext);
  const dataSourceLabel = getExportDataSourceDisplayName(exportContext?.selectedSource);
  const footnotesBlock = formatFootnotesBlockForTextExport(locationFootnotes);
  const all = [headers, ...rows];
  const cw  = headers.map((_, i) => Math.max(...all.map(r => String(r[i] ?? '').length)));
  const pad = (s, w) => String(s ?? '').padEnd(w);
  const lines = [
    title,
    '='.repeat(Math.min(title.length, 80)),
    '',
    ...(sortedByLine ? [sortedByLine, ''] : []),
    ...(mainTableTopHeading ? [mainTableTopHeading, ''] : []),
    headers.map((h, i) => pad(h, cw[i])).join('  '),
    cw.map((w) => '-'.repeat(w)).join('  '),
    ...rows.map((r) => r.map((v, i) => pad(v, cw[i])).join('  ')),
    '',
    ...(paginationSummary ? [paginationSummary, ''] : []),
    ...(footnotesBlock ? [footnotesBlock, ''] : []),
    ...(scoreFootnote ? [scoreFootnote, ''] : []),
    `Data Source: ${dataSourceLabel}`,
    `URL: ${sourceUrl}`,
    '',
    getKrimeExportCopyrightLine(),
  ];
  triggerDownload(lines.join('\n'), `${filename}.txt`, 'text/plain;charset=utf-8;');
}

// ─── Excel ────────────────────────────────────────────────────────────────────
export async function downloadExcel(exportContext) {
  const {
    filename,
    headers,
    rows,
    title,
    sortedByLine,
    mainTableTopHeading,
    paginationSummary,
    scoreFootnote,
    locationFootnotes,
  } = extractTableData(exportContext);
  if (!headers.length) return;
  const sourceUrl = buildExportSourceUrl(exportContext);
  const dataSourceLabel = getExportDataSourceDisplayName(exportContext?.selectedSource);
  const isReportsExport = exportContext.activeTab === 'reports';
  const footnotesBlock = formatFootnotesBlockForTextExport(locationFootnotes);
  const XLSX = await import('xlsx');
  const data = [[title], []];
  if (sortedByLine) {
    data.push([sortedByLine], []);
  }
  if (mainTableTopHeading) {
    data.push([mainTableTopHeading], []);
  }
  data.push(headers);
  if (isReportsExport) {
    data.push(['', '']);
  }
  data.push(...rows, []);
  if (paginationSummary) {
    data.push([paginationSummary], []);
  }
  if (footnotesBlock) {
    footnotesBlock.split('\n').forEach((line) => {
      data.push([line]);
    });
    data.push([]);
  }
  if (scoreFootnote) {
    data.push([scoreFootnote], []);
  }
  data.push([`Data Source: ${dataSourceLabel}`], [`URL: ${sourceUrl}`], [''], [getKrimeExportCopyrightLine()]);
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = headers.map((h, i) => ({ wch: Math.max(h.length + 2, ...rows.map(r => String(r[i] ?? '').length + 1)) }));
  XLSX.utils.book_append_sheet(wb, ws, title.substring(0, 31));
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/** html2canvas device-pixel scale — higher = sharper text/lines (PNG, PDF, print). */
const RASTER_HTML2CANVAS_SCALE = 3;
/** Letter-page fit scale for final canvas (higher = sharper when embedded in PDF / printed). */
const RASTER_LETTER_PIXEL_SCALE = 2;

/**
 * Mobile: AnalysisHeader is outside captureRef. Capture a deep-cloned header off-screen so the
 * visible header is never restyled (avoids flash/jump during PDF/PNG/print).
 */
async function captureMobileAnalysisHeaderForExport(scale, canonicalLocationName, footnoteNumbers = null) {
  const wrap = document.querySelector('.mobile-analysis-header');
  if (!wrap || window.getComputedStyle(wrap).display === 'none') return null;
  const target = wrap.querySelector('.analysis-header') || wrap;

  const liveTextDiv = target.querySelector('.location-title-container .auto-resize-container > div');
  let liveLocationText = null;
  let liveTitleStyle = null;
  const canonical = canonicalLocationName != null && String(canonicalLocationName).trim()
    ? String(canonicalLocationName).trim()
    : null;
  if (liveTextDiv) {
    liveTitleStyle = pickTitleSnapshotStyle(window.getComputedStyle(liveTextDiv));
    liveLocationText = canonical || liveTextDiv.innerText.replace(/\s+/g, ' ').trim();
  } else if (canonical) {
    liveLocationText = canonical;
  }

  let fnForRaster = footnoteNumbers;
  if (fnForRaster == null && liveTextDiv) {
    fnForRaster = readFootnoteNumbersFromLiveTitleDiv(liveTextDiv);
  }

  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${LETTER_PAGE_W}px`,
    `max-width:${LETTER_PAGE_W}px`,
    'margin:0',
    'padding:0',
    'box-sizing:border-box',
    'background:#ffffff',
    'z-index:-1',
    'pointer-events:none',
    'overflow:hidden',
  ].join(';');
  const clone = target.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);

  const approxH = Math.max(
    200,
    Math.ceil(Math.max(target.offsetHeight, target.getBoundingClientRect().height))
      + EXPORT_HTML2CANVAS_WINDOW_PAD_PX,
  );

  try {
    const { default: html2canvas } = await import('html2canvas');
    return await html2canvas(host, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: EXPORT_LAYOUT_VIEWPORT_W,
      windowHeight: approxH,
      onclone: (clonedDoc) => {
        applyExportFixesForMobileHeaderClone(clonedDoc, liveLocationText, liveTitleStyle, fnForRaster);
      },
    });
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * Desktop: AnalysisHeader lives inside split-view-content. For Location Name Scores chart, we capture
 * it off-screen (same as mobile) so the main capture can omit the header — matching mobile's
 * stacked [header, body] aspect ratio and avoiding letter-fit scale dominated by height (pillarboxing).
 */
async function captureDesktopAnalysisHeaderForExport(scale, canonicalLocationName, captureEl, footnoteNumbers = null) {
  const target = captureEl?.querySelector?.('.analysis-header');
  if (!target) return null;

  const liveTextDiv = target.querySelector('.location-title-container .auto-resize-container > div');
  let liveLocationText = null;
  let liveTitleStyle = null;
  const canonical = canonicalLocationName != null && String(canonicalLocationName).trim()
    ? String(canonicalLocationName).trim()
    : null;
  if (liveTextDiv) {
    liveTitleStyle = pickTitleSnapshotStyle(window.getComputedStyle(liveTextDiv));
    liveLocationText = canonical || liveTextDiv.innerText.replace(/\s+/g, ' ').trim();
  } else if (canonical) {
    liveLocationText = canonical;
  }

  let fnForRaster = footnoteNumbers;
  if (fnForRaster == null && liveTextDiv) {
    fnForRaster = readFootnoteNumbersFromLiveTitleDiv(liveTextDiv);
  }

  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${LETTER_PAGE_W}px`,
    `max-width:${LETTER_PAGE_W}px`,
    'margin:0',
    'padding:0',
    'box-sizing:border-box',
    'background:#ffffff',
    'z-index:-1',
    'pointer-events:none',
    'overflow:hidden',
  ].join(';');
  const clone = target.cloneNode(true);
  host.appendChild(clone);
  document.body.appendChild(host);

  const approxH = Math.max(
    200,
    Math.ceil(Math.max(target.offsetHeight, target.getBoundingClientRect().height))
      + EXPORT_HTML2CANVAS_WINDOW_PAD_PX,
  );

  try {
    const { default: html2canvas } = await import('html2canvas');
    return await html2canvas(host, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: EXPORT_LAYOUT_VIEWPORT_W,
      windowHeight: approxH,
      onclone: (clonedDoc) => {
        applyExportFixesForMobileHeaderClone(clonedDoc, liveLocationText, liveTitleStyle, fnForRaster);
      },
    });
  } finally {
    document.body.removeChild(host);
  }
}

/** Scale raster canvas to letter-width px (matches html2canvas output width). */
function scaleCanvasToWidth(canvas, targetW) {
  if (!canvas?.width || targetW < 1) return canvas;
  const targetH = Math.max(1, Math.round((canvas.height / canvas.width) * targetW));
  const out = document.createElement('canvas');
  out.width = targetW;
  out.height = targetH;
  const ctx = out.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, targetW, targetH);
  return out;
}

/**
 * html2canvas: omit KRIME/Leaflet UI chrome from the raster only (no live DOM changes).
 * Keep `.leaflet-control-attribution` (Leaflet / © OpenStreetMap / CARTO / etc.).
 */
function mapExportIgnoreChromeElement(node) {
  if (!(node instanceof HTMLElement)) return false;
  if (node.closest('.leaflet-control-attribution')) return false;
  return !!(
    node.closest('.leaflet-control-zoom')
    || node.closest('.map-export-actions')
    || node.closest('.map-minmax')
    || node.closest('.map-location')
    || node.closest('.map-layer')
    || node.closest('.data-selector-map-control')
  );
}

/**
 * Capture the map exactly as the user sees it by manually compositing
 * each Leaflet pane at the correct position using getBoundingClientRect
 * (which accounts for all CSS transforms including translate3d).
 *
 * This avoids html2canvas entirely for the map — html2canvas mishandles
 * Leaflet's translate3d on the SVG overlay pane, causing tile/overlay
 * misalignment.
 */
async function buildMapRasterCanvas(mapContainerEl, scale) {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  const rect = mapContainerEl.getBoundingClientRect();
  const w = Math.round(rect.width * scale);
  const h = Math.round(rect.height * scale);
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#f0eff1';
  ctx.fillRect(0, 0, rect.width, rect.height);

  // ── 1. Draw raster tile images ──────────────────────────────────────
  mapContainerEl.querySelectorAll('.leaflet-tile-pane img').forEach((img) => {
    if (!img.complete || !img.naturalWidth) return;
    const ir = img.getBoundingClientRect();
    const dx = ir.left - rect.left;
    const dy = ir.top - rect.top;
    const dw = ir.width;
    const dh = ir.height;
    if (dw < 1 || dh < 1) return;
    try {
      ctx.drawImage(img, dx, dy, dw, dh);
    } catch (_) { /* CORS */ }
  });

  // ── 2. Draw SVG overlays (GeoJSON polygons, selected borders, etc.) ─
  // Leaflet's overlay pane contains one large <svg> with translate3d.
  // getBoundingClientRect gives us the final screen position including
  // all transforms, so we just need to serialize and draw at that rect.
  const svgEls = mapContainerEl.querySelectorAll(
    '.leaflet-overlay-pane > svg, .leaflet-marker-pane > svg'
  );

  for (const svg of svgEls) {
    if (mapExportIgnoreChromeElement(svg)) continue;
    const svgRect = svg.getBoundingClientRect();
    const dx = svgRect.left - rect.left;
    const dy = svgRect.top - rect.top;
    const sw = svgRect.width;
    const sh = svgRect.height;
    if (sw < 1 || sh < 1) continue;

    try {
      // Deep-clone the SVG and inline all computed styles on shape elements
      // so it renders correctly when detached from the document
      const svgClone = svg.cloneNode(true);
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      // Set explicit dimensions matching the bounding rect
      svgClone.setAttribute('width', String(sw));
      svgClone.setAttribute('height', String(sh));

      // The original SVG uses a viewBox + translate3d to position itself.
      // We need to preserve the viewBox so internal coordinates map correctly,
      // but strip the CSS transform since we're positioning via drawImage.
      svgClone.style.transform = 'none';
      svgClone.style.webkitTransform = 'none';

      // Inline computed fill/stroke on all shape elements — CSS vars like
      // var(--navy) won't resolve when the SVG is serialized to a blob
      const shapeSelector = 'path, polygon, polyline, circle, rect, ellipse, line';
      const origShapes = svg.querySelectorAll(shapeSelector);
      const cloneShapes = svgClone.querySelectorAll(shapeSelector);
      origShapes.forEach((orig, i) => {
        const clone = cloneShapes[i];
        if (!clone) return;
        const cs = window.getComputedStyle(orig);
        clone.setAttribute('fill', cs.fill || 'none');
        clone.setAttribute('fill-opacity', cs.fillOpacity || '1');
        clone.setAttribute('stroke', cs.stroke || 'none');
        clone.setAttribute('stroke-width', cs.strokeWidth || '1');
        clone.setAttribute('stroke-opacity', cs.strokeOpacity || '1');
        clone.setAttribute('opacity', cs.opacity || '1');
        // Remove any dash array that's "none" (serializes badly)
        const da = cs.strokeDasharray;
        if (da && da !== 'none') {
          clone.setAttribute('stroke-dasharray', da);
        }
      });

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (e) => reject(e);
        image.src = url;
      });

      ctx.drawImage(img, dx, dy, sw, sh);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('KRIME map SVG overlay draw failed:', e);
    }
  }

  // ── 3. Draw any canvas elements (if present) ───────────────────────
  mapContainerEl.querySelectorAll('canvas').forEach((c) => {
    if (mapExportIgnoreChromeElement(c)) return;
    const cr = c.getBoundingClientRect();
    try {
      ctx.drawImage(c, cr.left - rect.left, cr.top - rect.top, cr.width, cr.height);
    } catch (_) { /* ignore */ }
  });

  // ── 4. Draw attribution text ────────────────────────────────────────
  const attr = mapContainerEl.querySelector('.leaflet-control-attribution');
  if (attr) {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const attrCanvas = await html2canvas(attr, {
        scale: 1,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const ar = attr.getBoundingClientRect();
      ctx.drawImage(
        attrCanvas,
        ar.left - rect.left,
        ar.top - rect.top,
        ar.width,
        ar.height,
      );
    } catch (_) { /* attribution is nice-to-have */ }
  }

  return out;
}

/**
 * PNG-only: screenshot the visible table panel (main-table-top + scroll viewport)
 * at LETTER_PAGE_W so it matches the AnalysisHeader width, with minimal layout
 * fixes for sticky columns, heading borders, and print-color-adjust.
 */
async function captureMainTableViewportForPng(captureEl, scale) {
  const scrollContainers = Array.from(
    captureEl.querySelectorAll('.main-table-scroll-container')
  ).filter(isLocationTypeOrHistoricalTableScrollContainer);
  if (!scrollContainers.length) return null;

  const scrollEl = scrollContainers[0];
  const mainTableTop = captureEl.querySelector('.main-table-top');

  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  try {
    const { default: html2canvas } = await import('html2canvas');

    let topCanvas = null;
    if (mainTableTop) {
      topCanvas = await html2canvas(mainTableTop, {
        scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll('svg').forEach((svg) => {
            const cls = svg.classList;
            if (cls.contains('lucide-chevron-down') || 
                svg.closest('.comparison-menu-button') || 
                svg.closest('.population-menu-button')) {
              svg.style.setProperty('display', 'none', 'important');
            }
          });
        },
      });
    }

    const tableCanvas = await html2canvas(scrollEl, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        applyTableRasterFlattenStylesInClone(clonedDoc);

        clonedDoc.querySelectorAll('.location-type-table th:nth-child(2), .location-type-table td:nth-child(2)').forEach((el) => {
          el.style.setProperty('border-right', '1px solid var(--border-color)', 'important');
        });

        clonedDoc.querySelectorAll('.location-type-table thead tr').forEach((tr) => {
          tr.style.setProperty('background', 'linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7)) var(--navy)', 'important');
        });

        clonedDoc.querySelectorAll('.location-type-table thead th').forEach((th) => {
          const tr = th.parentElement;
          if (!tr) return;
          const idx = Array.prototype.indexOf.call(tr.children, th);
          th.style.setProperty('background', 'transparent', 'important');
          th.style.setProperty('border-left', 'none', 'important');
          th.style.setProperty('border-top', 'none', 'important');
          th.style.setProperty('border-right', idx === 1 ? '1px solid var(--border-color)' : 'none', 'important');
          th.style.setProperty('border-bottom', '2px solid var(--border-color)', 'important');
          th.style.setProperty('box-shadow', 'none', 'important');
        });

        clonedDoc.querySelectorAll('.historical-table thead th').forEach((th) => {
          const tr = th.parentElement;
          if (!tr) return;
          const idx = Array.prototype.indexOf.call(tr.children, th);
          th.style.setProperty('background', 'transparent', 'important');
          th.style.setProperty('border-left', 'none', 'important');
          th.style.setProperty('border-top', 'none', 'important');
          th.style.setProperty('border-right', idx === 0 ? '1px solid var(--border-color)' : 'none', 'important');
          th.style.setProperty('border-bottom', '2px solid var(--border-color)', 'important');
          th.style.setProperty('box-shadow', 'none', 'important');
        });

        clonedDoc.querySelectorAll('.main-table thead tr, .main-table tbody tr').forEach((el) => {
          el.style.webkitPrintColorAdjust = 'exact';
          el.style.printColorAdjust = 'exact';
        });

        clonedDoc.querySelectorAll('.main-table tbody').forEach((el) => {
          el.style.setProperty('position', 'relative', 'important');
          el.style.setProperty('top', '-2px', 'important');
        });
      },
    });

    if (!tableCanvas || tableCanvas.width < 1 || tableCanvas.height < 1) return null;

    if (topCanvas && topCanvas.width > 0 && topCanvas.height > 0) {
      const maxW = Math.max(topCanvas.width, tableCanvas.width);
      const gapH = Math.round(10 * scale);

      // Center both pieces on a shared-width canvas
      const out = document.createElement('canvas');
      out.width = maxW;
      out.height = gapH + topCanvas.height + tableCanvas.height;
      const ctx = out.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, out.width, out.height);

      const topX = Math.round((maxW - topCanvas.width) / 2);
      ctx.drawImage(topCanvas, topX, gapH);

      const tableX = Math.round((maxW - tableCanvas.width) / 2);
      ctx.drawImage(tableCanvas, tableX, gapH + topCanvas.height);

      return out;
    }

    return tableCanvas;
  } catch (e) {
    console.warn('KRIME viewport table PNG failed:', e);
    return null;
  }
}

// ─── Table / chart / map raster (shared by PDF, PNG, Print) ────────────────────
async function buildTableCaptureCanvas(exportContext, scale, letterPixelScale = 1, options = {}) {
  const { fullTableRaster = false, mapContainerEl = null, viewportTablePng = false } = options;
  const captureEl = exportContext?.captureRef?.current;
  if (!captureEl) return null;
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  let canonicalLocation = null;
  if (exportContext) {
    try {
      const display = formatExportLocationDisplay(exportContext);
      if (display && String(display).trim()) canonicalLocation = String(display).trim();
    } catch (_) {
      /* ignore */
    }
    if (!canonicalLocation && exportContext?.locationName != null && String(exportContext.locationName).trim()) {
      canonicalLocation = String(exportContext.locationName).trim();
    }
  }

  let footnoteNumbersForRaster = null;
  try {
    const map = getLocationFootnotesMap(exportContext?.locationData);
    if (map && Object.keys(map).length) {
      footnoteNumbersForRaster = getSortedFootnoteIndicesFromMap(map);
    }
  } catch (_) {
    footnoteNumbersForRaster = null;
  }

  const pad = Math.round(16 * scale);
  const mobileHeaderCanvas = await captureMobileAnalysisHeaderForExport(
    scale,
    canonicalLocation,
    footnoteNumbersForRaster,
  );
  const useDesktopHeaderSplit =
    !mobileHeaderCanvas &&
    fullTableRaster === false &&
    !!captureEl.querySelector('.analysis-header') &&
    (mapContainerEl || viewportTablePng || shouldCenterLocationNameScoresChartExtract(exportContext));

  let desktopHeaderCanvas = null;
  if (useDesktopHeaderSplit) {
    desktopHeaderCanvas = await captureDesktopAnalysisHeaderForExport(
      scale,
      canonicalLocation,
      captureEl,
      footnoteNumbersForRaster,
    );
  }

  const captureOpts = { fullTableRaster, footnoteNumbers: footnoteNumbersForRaster };
  let content = null;
  let rowIntervalsCss = null;
  let trimmedHeaderForStack = null;
  if (mapContainerEl) {
    const targetW = Math.round(LETTER_PAGE_W * scale);
    const rawMap = await buildMapRasterCanvas(mapContainerEl, scale);
    const mapScaled = trimCanvasBottom(scaleCanvasToWidth(rawMap, targetW), pad);
    if (mobileHeaderCanvas) {
      trimmedHeaderForStack = trimCanvasBottom(mobileHeaderCanvas, pad);
      content = stackVertical([trimmedHeaderForStack, mapScaled]);
    } else if (desktopHeaderCanvas) {
      trimmedHeaderForStack = trimCanvasBottom(desktopHeaderCanvas, pad);
      content = stackVertical([trimmedHeaderForStack, mapScaled]);
    } else {
      content = mapScaled;
    }
    rowIntervalsCss = null;
  } else if (viewportTablePng) {
    try {
      const rawTable = await captureMainTableViewportForPng(captureEl, scale);
      if (rawTable && rawTable.width > 0 && rawTable.height > 0) {
        const targetW = Math.round(LETTER_PAGE_W * scale);
        const trimmed = trimCanvasBottom(rawTable, pad);
        const centered = document.createElement('canvas');
        centered.width = targetW;
        centered.height = trimmed.height;
        const cCtx = centered.getContext('2d');
        cCtx.fillStyle = '#ffffff';
        cCtx.fillRect(0, 0, targetW, trimmed.height);
        const cx = Math.round((targetW - trimmed.width) / 2);
        cCtx.drawImage(trimmed, Math.max(0, cx), 0);
        const tableScaled = centered;
        if (mobileHeaderCanvas) {
          trimmedHeaderForStack = trimCanvasBottom(mobileHeaderCanvas, pad);
          content = stackVertical([trimmedHeaderForStack, tableScaled]);
        } else if (desktopHeaderCanvas) {
          trimmedHeaderForStack = trimCanvasBottom(desktopHeaderCanvas, pad);
          content = stackVertical([trimmedHeaderForStack, tableScaled]);
        } else {
          content = tableScaled;
        }
        rowIntervalsCss = null;
      }
    } catch (e) {
      console.warn('KRIME viewport table PNG pipeline failed, falling back to full capture:', e);
    }
  }
  if (content == null && !mapContainerEl) {
    if (mobileHeaderCanvas) {
      trimmedHeaderForStack = trimCanvasBottom(mobileHeaderCanvas, pad);
      const { canvas: rawData, rowIntervalsCss: ric } = await captureElement(
        captureEl,
        scale,
        canonicalLocation,
        captureOpts,
      );
      rowIntervalsCss = ric;
      const trimmedData = fullTableRaster ? rawData : trimCanvasBottom(rawData, pad);
      content = stackVertical([trimmedHeaderForStack, trimmedData]);
    } else if (desktopHeaderCanvas) {
      trimmedHeaderForStack = trimCanvasBottom(desktopHeaderCanvas, pad);
      const { canvas: rawData, rowIntervalsCss: ric } = await captureElement(
        captureEl,
        scale,
        canonicalLocation,
        { ...captureOpts, hideAnalysisHeaderInClone: true },
      );
      rowIntervalsCss = ric;
      const trimmedData = fullTableRaster ? rawData : trimCanvasBottom(rawData, pad);
      content = stackVertical([trimmedHeaderForStack, trimmedData]);
    } else {
      const { canvas: rawData, rowIntervalsCss: ric } = await captureElement(
        captureEl,
        scale,
        canonicalLocation,
        captureOpts,
      );
      rowIntervalsCss = ric;
      content = rawData;
    }
  }

  const trimmed = fullTableRaster
    ? trimFullTableRasterContentCanvas(
      content,
      rowIntervalsCss,
      scale,
      trimmedHeaderForStack ? trimmedHeaderForStack.height : 0,
      pad,
    )
    : trimCanvasBottom(content, pad);
  const headerOffsetPx = trimmedHeaderForStack ? trimmedHeaderForStack.height : 0;
  const rowIntervalsCanvas = (fullTableRaster && rowIntervalsCss && rowIntervalsCss.length)
    ? rowIntervalsCss
      .map(({ top, bottom }) => ({
        y0: Math.round(headerOffsetPx + top * scale),
        y1: Math.round(headerOffsetPx + bottom * scale),
      }))
      .map(({ y0, y1 }) => ({
        y0,
        y1: Math.min(y1, trimmed.height),
      }))
      .filter(({ y0, y1 }) => y1 > y0 + 0.5)
    : [];

  const sourceUrl = buildExportSourceUrl(exportContext);
  const gap20 = Math.round(20 * scale);

  let exportMeta = null;
  try {
    exportMeta = extractTableData(exportContext);
  } catch (_) {
    exportMeta = {};
  }

  const stackParts = [trimmed];
  if (viewportTablePng) {
    const tableFooterGap = Math.round(20 * scale);
    const gapCanvas = document.createElement('canvas');
    gapCanvas.width = trimmed.width;
    gapCanvas.height = tableFooterGap;
    const gCtx = gapCanvas.getContext('2d');
    gCtx.fillStyle = '#ffffff';
    gCtx.fillRect(0, 0, gapCanvas.width, tableFooterGap);
    stackParts.push(gapCanvas);
  }
  let hasPaginationFooter = false;
  let pCanvas = null;
  if (fullTableRaster) {
    const { paginationSummary, paginationLineParts } = exportMeta;
    if (paginationSummary && String(paginationSummary).trim()) {
      pCanvas = buildPaginationFooterCanvas(
        trimmed.width,
        scale,
        paginationLineParts,
        paginationSummary.trim(),
      );
      if (pCanvas) {
        stackParts.push(pCanvas);
        hasPaginationFooter = true;
      }
    }
  }
  const { scoreFootnote, locationFootnotes } = exportMeta;
  let fnCanvas = null;
  if (locationFootnotes && Object.keys(locationFootnotes).length) {
    fnCanvas = buildLocationFootnotesCanvas(
      trimmed.width,
      scale,
      locationFootnotes,
      {
        gapTopPx: hasPaginationFooter ? gap20 : Math.round(10 * scale),
      },
    );
    if (fnCanvas) {
      stackParts.push(fnCanvas);
    }
  }
  let hasScoreFootnote = false;
  let sfCanvas = null;
  if (scoreFootnote && String(scoreFootnote).trim()) {
    sfCanvas = buildScoreFootnoteCanvas(
      trimmed.width,
      scale,
      scoreFootnote.trim(),
      {
        gapTopPx: (fnCanvas || hasPaginationFooter) ? gap20 : Math.round(10 * scale),
      },
    );
    if (sfCanvas) {
      stackParts.push(sfCanvas);
      hasScoreFootnote = true;
    }
  }
  const hasFooterBeforeSource = hasPaginationFooter || hasScoreFootnote || !!fnCanvas;
  const dataSourceLabel = getExportDataSourceDisplayName(exportContext?.selectedSource);
  const source = buildSourceCanvas(
    trimmed.width,
    scale,
    sourceUrl,
    hasFooterBeforeSource
      ? { gapTopPx: gap20, dataSourceLabel }
      : { dataSourceLabel },
  );
  stackParts.push(source);
  const stacked = stackVertical(stackParts);
  const stackedForLetter =
    fullTableRaster ? stacked : padOrCropCanvasToLetterRasterWidth(stacked, scale);
  if (fullTableRaster) {
    const tableEndY = trimmed.height;
    const paginationEndY = hasPaginationFooter && pCanvas ? tableEndY + pCanvas.height : tableEndY;
    const footerBlockStartY = hasPaginationFooter && pCanvas ? tableEndY + pCanvas.height : tableEndY;
    const sourceBlockStartY = stacked.height - source.height;
    const sliceMeta = {
      rowIntervals: rowIntervalsCanvas,
      tableEndY,
      footerStartY: tableEndY,
      footerBlockStartY,
      paginationEndY,
      sourceStartY: sourceBlockStartY < stacked.height ? sourceBlockStartY : null,
      ih: stacked.height,
    };
    stacked.__krimeSliceMeta = sliceMeta;
    return { canvas: stacked, sliceMeta };
  }
  const isLmScoresChart = shouldCenterLocationNameScoresChartExtract(exportContext);
  const isMapRasterExport = !!mapContainerEl;
  const isViewportTablePngExport = !!viewportTablePng;
  /** Same layout as LocationNameScoresChart: header + footnotes/methodology/source at letter width; only the middle slice (chart, map, or viewport table) may shrink vertically to fit one page. */
  const useSplitLetterFit =
    !fullTableRaster
    && stackedForLetter.width > 0
    && ((isLmScoresChart && trimmedHeaderForStack) || isMapRasterExport || isViewportTablePngExport);

  let letterCanvas;
  if (useSplitLetterFit) {
    const PAGE_W = Math.round(LETTER_PAGE_W * letterPixelScale);
    const PAGE_H = Math.round(LETTER_PAGE_H * letterPixelScale);
    const sw = stackedForLetter.width;

    const tailFooterPx =
      (pCanvas ? pCanvas.height : 0)
      + (fnCanvas ? fnCanvas.height : 0)
      + (sfCanvas ? sfCanvas.height : 0)
      + source.height;

    const fixedScale = PAGE_W / sw;
    const headerStripH = trimmedHeaderForStack ? trimmedHeaderForStack.height : 0;
    const headerH = Math.round(headerStripH * fixedScale);
    const footerH = Math.round(tailFooterPx * fixedScale);
    const availableH = PAGE_H - headerH - footerH;

    const bodyTopPx = headerStripH;
    const bodyBottomPx = stackedForLetter.height - tailFooterPx;
    const bodyH = bodyBottomPx - bodyTopPx;

    const fallbackLetter = () =>
      fitCanvasToLetterPortrait(stackedForLetter, letterPixelScale, {
        center: isLmScoresChart || isMapRasterExport || isViewportTablePngExport,
        centerHorizontal: isLmScoresChart || isMapRasterExport || isViewportTablePngExport,
      });

    if (!Number.isFinite(bodyH) || bodyH <= 0 || availableH <= 0) {
      letterCanvas = fallbackLetter();
    } else {
      const bodyScale = Math.min(fixedScale, availableH / bodyH);
      const bodyDrawH = Math.round(bodyH * bodyScale);
      const bodyDrawW = Math.round(sw * bodyScale);

      letterCanvas = document.createElement('canvas');
      letterCanvas.width = PAGE_W;
      letterCanvas.height = PAGE_H;
      const ctx = letterCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, PAGE_W, PAGE_H);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      if (headerH > 0 && headerStripH > 0) {
        ctx.drawImage(stackedForLetter, 0, 0, sw, headerStripH, 0, 0, PAGE_W, headerH);
      }

      const bodyX = Math.round((PAGE_W - bodyDrawW) / 2);
      ctx.drawImage(stackedForLetter, 0, bodyTopPx, sw, bodyH, bodyX, headerH, bodyDrawW, bodyDrawH);

      const footerSrcH = stackedForLetter.height - bodyBottomPx;
      const footerY = headerH + bodyDrawH;
      ctx.drawImage(stackedForLetter, 0, bodyBottomPx, sw, footerSrcH, 0, footerY, PAGE_W, footerH);
    }
  } else {
    letterCanvas = fitCanvasToLetterPortrait(stackedForLetter, letterPixelScale, {
      center: isLmScoresChart,
      centerHorizontal: isLmScoresChart,
    });
  }
  return { canvas: letterCanvas, sliceMeta: null };
}

// ─── PDF (table / chart views) ────────────────────────────────────────────────
// Captures .split-view-content (AnalysisHeader + DataView) exactly as rendered
// on screen. The onclone callback hides chrome and fixes html2canvas quirks
// before the canvas is drawn. Trailing blank space is trimmed and a source
// footer strip is appended below.
export async function downloadPDF(exportContext) {
  let { filename } = extractTableData(exportContext);
  if (exportContext.selectedView === 'chart') filename = filename.replace(/\s+Data$/, ' Chart');

  const fullTable = shouldUseFullTableRasterExport(exportContext);
  const capture = await buildTableCaptureCanvas(
    exportContext,
    RASTER_HTML2CANVAS_SCALE,
    RASTER_LETTER_PIXEL_SCALE,
    { fullTableRaster: fullTable },
  );
  if (!capture) {
    console.warn('KRIME Export: captureRef missing');
    return;
  }
  const { canvas: composite, sliceMeta } = capture;
  if (fullTable && exportContext.selectedView === 'table') {
    await embedCanvasInPDFTiled(composite, filename, sliceMeta);
  } else {
    await embedCanvasInPDF(composite, filename);
  }
}

// ─── PNG (table / chart views) ────────────────────────────────────────────────
export async function downloadPNG(exportContext) {
  let { filename } = extractTableData(exportContext);
  if (exportContext.selectedView === 'chart') filename = filename.replace(/\s+Data$/, ' Chart');

  const viewportTablePng = shouldUseViewportTablePngExport(exportContext);
  const capture = await buildTableCaptureCanvas(
    exportContext,
    RASTER_HTML2CANVAS_SCALE,
    RASTER_LETTER_PIXEL_SCALE,
    { fullTableRaster: false, viewportTablePng },
  );
  if (!capture) {
    console.warn('KRIME Export: captureRef missing');
    return;
  }
  const { canvas: composite } = capture;
  if (!composite?.width || !composite?.height) {
    console.warn('KRIME Export: PNG composite canvas has zero size');
    return;
  }
  const link = document.createElement('a');
  try {
    link.href = composite.toDataURL('image/png');
  } catch (e) {
    console.error('KRIME Export: PNG toDataURL failed (invalid or tainted canvas)', e);
    return;
  }
  link.download = `${filename}.png`;
  link.click();
}

// ─── Map PDF / PNG (same raster pipeline as table/chart; body = visible map) ──

export async function downloadMapPDF(exportContext, mapContainerEl) {
  let { filename } = extractTableData(exportContext);
  filename = filename.replace(/\s+Data$/, ' Map');
  const capture = await buildTableCaptureCanvas(
    exportContext,
    RASTER_HTML2CANVAS_SCALE,
    RASTER_LETTER_PIXEL_SCALE,
    { fullTableRaster: false, mapContainerEl },
  );
  if (!capture) {
    console.warn('KRIME Export: map raster capture failed (captureRef or map container)');
    return;
  }
  await embedCanvasInPDF(capture.canvas, filename);
}

export async function downloadMapPNG(exportContext, mapContainerEl) {
  let { filename } = extractTableData(exportContext);
  filename = filename.replace(/\s+Data$/, ' Map');
  const capture = await buildTableCaptureCanvas(
    exportContext,
    RASTER_HTML2CANVAS_SCALE,
    RASTER_LETTER_PIXEL_SCALE,
    { fullTableRaster: false, mapContainerEl },
  );
  if (!capture) {
    console.warn('KRIME Export: map raster capture failed (captureRef or map container)');
    return;
  }
  const link = document.createElement('a');
  link.href = capture.canvas.toDataURL('image/png');
  link.download = `${filename}.png`;
  link.click();
}

// ─── Print ────────────────────────────────────────────────────────────────────
function buildPrintSlicesFromCanvas(canvas, sliceMetaOverride = null) {
  const m = EXPORT_PDF_PRINT_MARGIN_PT;
  const contentW = LETTER_PAGE_W_PT - 2 * m;
  const contentH = LETTER_PAGE_H_PT - 2 * m;
  const iw = canvas.width;
  const ih = canvas.height;
  if (!iw || !ih) return [];
  const destHTotal = (ih / iw) * contentW;
  const sliceMeta = sliceMetaOverride ?? canvas.__krimeSliceMeta ?? null;
  let ranges = sliceMeta
    ? buildFullTableTiledSliceRanges(ih, contentH, destHTotal, sliceMeta)
    : null;
  if (ranges?.length && sliceMeta) {
    ranges = mergeFooterRangesWhenNoRowSplit(ranges, sliceMeta);
  }

  const pushSlice = (slices, srcY, srcH) => {
    const sliceHPt = (srcH / ih) * destHTotal;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = iw;
    sliceCanvas.height = Math.max(1, Math.ceil(srcH));
    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, srcY, iw, srcH, 0, 0, iw, srcH);
    let widthPt = contentW;
    let heightPt = sliceHPt;
    if (heightPt > contentH + 0.5) {
      const s = Math.min(1, contentH / sliceHPt);
      widthPt = contentW * s;
      heightPt = sliceHPt * s;
    }
    slices.push({ dataUrl: sliceCanvas.toDataURL('image/png'), widthPt, heightPt });
  };

  const slices = [];
  if (ranges && ranges.length) {
    for (const { srcY, srcH } of ranges) {
      pushSlice(slices, srcY, srcH);
      if (slices.length > 500) break;
    }
    return slices;
  }

  let srcY = 0;
  while (srcY < ih - 0.001) {
    const remainingPx = ih - srcY;
    const remainingPt = (remainingPx / ih) * destHTotal;
    const sliceHPt = Math.min(contentH, remainingPt);
    let srcH = (sliceHPt / destHTotal) * ih;
    if (srcY + srcH > ih) srcH = remainingPx;
    srcH = Math.min(remainingPx, Math.max(1, srcH));
    if (srcH < 1 && remainingPx > 0) srcH = remainingPx;
    pushSlice(slices, srcY, srcH);
    srcY += srcH;
    if (slices.length > 500) break;
  }
  return slices;
}

/** Table/chart/map: raster in a hidden iframe — full table tiles like PDF when needed. */
export async function triggerPrint(exportContext, options = {}) {
  const { mapContainerEl } = options;
  if (!exportContext?.captureRef?.current) {
    window.print();
    return;
  }
  const fullTable = mapContainerEl ? false : shouldUseFullTableRasterExport(exportContext);
  let capture;
  try {
    capture = await buildTableCaptureCanvas(
      exportContext,
      RASTER_HTML2CANVAS_SCALE,
      RASTER_LETTER_PIXEL_SCALE,
      mapContainerEl
        ? { fullTableRaster: false, mapContainerEl }
        : { fullTableRaster: fullTable },
    );
  } catch (err) {
    console.error('KRIME Print:', err);
    window.print();
    return;
  }
  if (!capture) {
    window.print();
    return;
  }
  const { canvas: composite, sliceMeta } = capture;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'KRIME print';
  iframe.style.cssText =
    'position:absolute;left:-10000px;top:0;width:8.5in;height:11in;border:0;opacity:0;pointer-events:none;overflow:hidden';
  document.body.appendChild(iframe);

  if (fullTable && exportContext.selectedView === 'table') {
    const slices = buildPrintSlicesFromCanvas(composite, sliceMeta);
    if (slices.length > 0) {
      const imgs = slices
        .map(
          (s, i) =>
            `<div class="print-page-wrap" style="width:${s.widthPt}pt;height:${s.heightPt}pt;margin:0 auto;box-sizing:border-box;overflow:hidden;page-break-after:${i < slices.length - 1 ? 'always' : 'auto'};page-break-inside:avoid;break-inside:avoid"><img class="print-page" src="${s.dataUrl}" alt="" style="display:block;width:100%;height:100%;object-fit:fill;object-position:top left;-webkit-print-color-adjust:exact;print-color-adjust:exact"/></div>`,
        )
        .join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>KRIME</title><style>
@page{margin:${EXPORT_PDF_PRINT_MARGIN_PT}pt;size:letter portrait}
html,body{margin:0;padding:0;background:#fff}
body{box-sizing:border-box}
</style></head><body>${imgs}</body></html>`;
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      const runPrintTiled = () => {
        const w = iframe.contentWindow;
        if (!w) {
          cleanup();
          return;
        }
        w.addEventListener('afterprint', cleanup);
        w.focus();
        w.print();
        setTimeout(cleanup, 3000);
      };

      iframe.onload = () => {
        URL.revokeObjectURL(objectUrl);
        setTimeout(runPrintTiled, 200);
      };
      iframe.src = objectUrl;
      return;
    }
  }

  {
    const dataUrl = composite.toDataURL('image/png');
    const shell = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>KRIME</title><style>
@page{margin:0;size:letter portrait}
html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;background:#fff}
img{display:block;width:100%;height:100%;object-fit:contain;object-position:top left;page-break-inside:avoid;-webkit-print-color-adjust:exact;print-color-adjust:exact}
</style></head><body></body></html>`;
    const blob = new Blob([shell], { type: 'text/html;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const runPrint = () => {
      const w = iframe.contentWindow;
      if (!w) {
        cleanup();
        return;
      }
      w.addEventListener('afterprint', cleanup);
      w.focus();
      w.print();
      setTimeout(cleanup, 3000);
    };

    iframe.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const doc = iframe.contentDocument;
      if (!doc?.body) {
        cleanup();
        return;
      }
      const img = doc.createElement('img');
      img.src = dataUrl;
      img.alt = '';
      doc.body.appendChild(img);
      if (img.complete) setTimeout(runPrint, 150);
      else img.onload = () => setTimeout(runPrint, 150);
    };
    iframe.src = objectUrl;
  }
}

// ─── Internal ─────────────────────────────────────────────────────────────────
function triggerDownload(content, filename, mimeType, options = {}) {
  const { prependUtf8Bom = false } = options;
  const payload = prependUtf8Bom ? `\uFEFF${content}` : content;
  const blob = new Blob([payload], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.setAttribute('download', filename);
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
